<template>
    <div>
        <b-modal
            id="createUpdateRecord"
            ref="createUpdateRecord"
            :lazy="true"
            @shown="shown"
            @hidden="hidden"
            @input="onChange"
            :scrollable="true"
            :title="title"
            no-close-on-backdrop
            title-tag="h2"
            :hide-backdrop="hideBackdrop"
            content-class="overflow-visible"
        >
            <b-form>
                <InvoiceInPaymentWarning
                    :invoiceId="data.bill_to_invoice"
                    :module="page === 'invoice' ? 'invoice' : 'medical-record'"
                />

                <b-alert show variant="warning" v-if="is_locked">
                    {{ locked.message }}
                </b-alert>

                <b-alert
                    v-for="(alertMessage, idx) in alertMessages"
                    :class="idx < alertMessages.length - 1 ? 'mb-2' : ''"
                    :key="idx"
                    :show="showAlert"
                    variant="warning"
                >
                    <div>
                        <i class="cus-error"></i>
                        {{ alertMessage }}
                    </div>
                </b-alert>

                <div
                    class="alert alert-warning"
                    role="alert"
                    v-if="data.item && data.item.length > 1"
                >
                    <strong>Next Due</strong> will be applied based on each
                    item's settings
                </div>

                <div
                    class="alert alert-warning"
                    role="alert"
                    v-if="displaySyncAlert"
                    v-html="vetsourceSyncAlertMessage"
                ></div>

                <div
                    class="alert alert-warning"
                    role="alert"
                    v-if="displayPatientAlert"
                    v-html="patientAlertMessage"
                ></div>

                <div
                    class="alert alert-warning"
                    role="alert"
                    v-if="displayClientAlert"
                >
                    The Home Delivery recommendation cannot be made because the
                    primary owner does not have {{ ownerAlertMessage }} on file.
                    Please click
                    <router-link
                        :to="{
                            name: 'client_profile',
                            params: { id: client_encoded_id },
                            query: { isEditMode: true }
                        }"
                        :target="'_blank'"
                    >
                        here
                    </router-link>
                    to add
                </div>
                <b-form-group
                    v-if="isShowSearchVetsourceProductCatalog"
                    :label-cols="4"
                >
                    <div class="d-flex align-items-start">
                        <input
                            id="search_vetsource_product_catalog"
                            type="checkbox"
                            class="mr-1 mt-1"
                            v-model="searchOnlinePharmacyOnly"
                        />
                        <label
                            for="search_vetsource_product_catalog"
                            class="font-weight-normal m-0"
                            >Search Vetsource Product Catalog</label
                        >
                    </div>
                </b-form-group>

                <!-- Apply item rule checkbox -->
                <b-form-group v-if="isShowApplyItemRule" :label-cols="4">
                    <apply-item-rule-checkbox
                        v-model="applyItemRule"
                        @change="onApplyItemRuleToggle"
                        :disabled="searchOnlinePharmacyOnly"
                    />
                </b-form-group>

                <b-form-group
                    v-if="editable && !is_paid"
                    :label-for="`item_id`"
                    :label-cols="4"
                    label="Item:"
                >
                    <vet-select
                        id="item"
                        ref="select-item"
                        v-model="data.item"
                        v-validate="'required'"
                        :class="{
                            'border-danger': errors.has('item'),
                            rounded: true
                        }"
                        :excluded-id="_.isArray(data.item) ? data.item : null"
                        :multiple="searchOnlinePharmacyOnly ? false : multiple"
                        :module="vetItemModule"
                        :key="applyItemRule"
                        :selectProps="vetItemProps"
                        :selectable="handleSelectable"
                        :showDisabledTooltip="true"
                        @options-loaded="onOptionsLoaded"
                        :filterByOnlinePharmacy="searchOnlinePharmacyOnly"
                        :disabled="isLoading"
                        :patientId="data.patient_id"
                        placeholder="Item (required)"
                        name="item"
                    />
                </b-form-group>

                <b-form-group
                    v-else
                    :label-for="`item_id`"
                    :label-cols="4"
                    label="Item:"
                    class="item"
                >
                    <div v-if="data.item" class="col-form-label">
                        <div v-if="data.item.length > 1">
                            <div v-for="item in data.item" :key="item.id">
                                {{ item.label }}
                            </div>
                        </div>
                        <div v-else>
                            {{
                                Array.isArray(data.item)
                                    ? data.item[0].label
                                    : data.item.label
                            }}
                        </div>
                    </div>
                </b-form-group>

                <div v-if="searchOnlinePharmacyOnly">
                    <new-recommendation
                        ref="new-recommendation"
                        :value="data"
                        :key="key"
                        @update="updateRecommendationData"
                        :isLocked="is_locked"
                        :providers="providers"
                        :givenByOptions="givenByOptions"
                        :datePickerOptions="datePickerOptions"
                        :medicalNotes="medicalNotes"
                        :formError="formError"
                        :instruction-count="instructionCount"
                        :record-note-count="recordNoteCount"
                        :personal-note-count="personalNoteCount"
                    />
                </div>

                <div v-else>
                    <b-form-group
                        label="Provider:"
                        class
                        label-cols="4"
                        label-for="staff_id"
                    >
                        <v-select
                            id="staffs"
                            ref="vselect"
                            v-validate="'required'"
                            :disabled="is_locked"
                            :class="{
                                'border-danger':
                                    errors.has('staffs') ||
                                    formError.includes('staff_id')
                            }"
                            :options="providers"
                            v-model="data.staff_id"
                            :label="'name'"
                            name="staffs"
                            aria-placeholder="true"
                            placeholder="Provider (required)"
                        ></v-select>
                    </b-form-group>

                    <b-form-group
                        class="med-record"
                        label="Given By:"
                        label-cols="4"
                        label-for="given_by"
                    >
                        <v-select
                            id="given_by"
                            :disabled="is_locked"
                            ref="vselect"
                            :options="givenByOptions"
                            v-model="data.given_by"
                            :label="'name'"
                            name="given_by"
                            aria-placeholder="true"
                            placeholder="Given By (optional)"
                        ></v-select>
                    </b-form-group>

                    <b-form-group
                        class="grooming"
                        label="Open Diagnoses:"
                        label-cols="4"
                        label-for="diagnoses"
                    >
                        <v-select
                            id="diagnoses"
                            :disabled="is_locked"
                            ref="vselect"
                            v-model="data.diagnosis"
                            :options="diagnosisFilter"
                            :label="'text'"
                            name="diagnoses"
                            multiple
                            aria-placeholder="true"
                            placeholder="Open Diagnoses (optional)"
                        ></v-select>
                    </b-form-group>

                    <b-form-group
                        :label-for="`given_date`"
                        :label-cols="4"
                        :label="lab_record ? 'Ordered:' : 'Given:'"
                    >
                        <vet-datetime
                            id="given_date"
                            :disabled="is_locked"
                            v-model="data.given_date"
                            v-validate="'required'"
                            :placeholder="{
                                date: 'Date (required)',
                                time: 'Time (required)'
                            }"
                            container="#createUpdateRecord"
                            rules="required"
                            name="given_date"
                            format="moment"
                        ></vet-datetime>
                    </b-form-group>

                    <b-form-group
                        :label-for="`next_due`"
                        label="Next Due:"
                        label-cols="4"
                    >
                        <date-picker
                            @dp-show="showDatePicker"
                            :config="datePickerOptions"
                            :disabled="
                                is_locked || (data.item && data.item.length > 1)
                            "
                            v-model="data.next_due"
                            placeholder="Next Due (optional)"
                            class="form-control"
                            name="next_due"
                        ></date-picker>
                    </b-form-group>

                    <b-form-group
                        class="med-record quantity"
                        :label-for="`quantity`"
                        :label-cols="4"
                        label="Quantity:"
                    >
                        <div class="form-inline">
                            <div v-if="!is_paid">
                                <b-form-input
                                    :disabled="is_locked || isQuantityDisabled"
                                    id="quantity"
                                    v-model="data.quantity"
                                    v-validate="'required'"
                                    :class="{
                                        'border-danger': errors.has('quantity')
                                    }"
                                    name="quantity"
                                    placeholder="Quantity (required)"
                                    class="col mr-auto"
                                ></b-form-input>
                                <span class="ml-1">
                                    {{ data.uom ? data.uom : "Unit(s)" }}
                                </span>
                            </div>
                            <div
                                v-else
                                class="col-form-label"
                                style="width: 85.5%;"
                            >
                                {{ data.quantity }} {{ data.uom }}
                            </div>
                            <dosage
                                :data="{
                                    id:
                                        data.item_type !=
                                            ITEMTYPES.TYPES.TYPE_LAB &&
                                        data.item
                                            ? data.item.id
                                            : null
                                }"
                                :weight-data="{
                                    weight: weight.weight,
                                    uom: weight.uom
                                }"
                                :item-id="
                                    data.item_type !=
                                        ITEMTYPES.TYPES.TYPE_LAB && data.item
                                        ? data.item.id
                                        : null
                                "
                                target="quantity"
                                parent-id="createUpdateRecord"
                                class="btn-sm ml-1"
                                style="position: absolute; right: 0;"
                            />
                        </div>
                    </b-form-group>

                    <b-form-group
                        v-if="
                            (isCubexItem(data.item) || isCubex) &&
                            cubexItemDispensingReference.dispensed
                        "
                        :label-for="`dispensed`"
                        :label-cols="4"
                        label="Dispensed:"
                    >
                        <div class="form-inline">
                            <div class="col-form-label">
                                {{ cubexItemDispensingReference.dispensed }}
                                {{ data.uom }}
                            </div>
                        </div>
                    </b-form-group>

                    <b-form-group
                        v-if="
                            (isCubexItem(data.item) || isCubex) &&
                            cubexItemDispensingReference.returned
                        "
                        :label-for="`returned`"
                        :label-cols="4"
                        label="Returned:"
                    >
                        <div class="form-inline">
                            <div class="col-form-label">
                                {{ cubexItemDispensingReference.returned }}
                                {{ data.uom }}
                            </div>
                        </div>
                    </b-form-group>

                    <b-form-group
                        v-if="
                            (isCubexItem(data.item) || isCubex) &&
                            cubexItemDispensingReference.waste
                        "
                        :label-for="`waste`"
                        :label-cols="4"
                        label="Waste:"
                    >
                        <div class="form-inline">
                            <div class="col-form-label">
                                {{ cubexItemDispensingReference.waste }}
                                {{ data.uom }}
                            </div>
                        </div>
                    </b-form-group>

                    <b-form-group
                        v-show="med_record_rad"
                        :label-for="`send_to_device`"
                        :label-cols="4"
                        label="Send to Device:"
                    >
                        <b-form-select
                            v-model="data.send_to_device"
                            name="send_to_device"
                            :options="sendToDeviceOptions"
                            :label="'text'"
                        ></b-form-select>
                    </b-form-group>

                    <b-form-group
                        class="lab-record source"
                        v-if="
                            checkItemType(
                                data.item_type,
                                ITEMTYPES.TYPES.TYPE_LAB
                            ) &&
                            originalSourcesLength &&
                            originalSourcesLength > 1
                        "
                        :label-for="`source`"
                        :label-cols="4"
                        label="Source:"
                    >
                        <b-form-select
                            v-model="data.source"
                            :disabled="is_locked"
                            :options="sourceOptions"
                            :label="'text'"
                            name="source"
                        ></b-form-select>
                    </b-form-group>

                    <b-form-group
                        class="lab-record send-to-lab"
                        :disabled="
                            is_locked || isHeskaCloud == 1 || isDisabledSendTo
                        "
                        label-class="font-weight-bold"
                        :label-cols="4"
                        id="send-to-lab"
                        :label="sendToLabLabel"
                        v-show="!hideSendToLab"
                        v-if="showSendToLab"
                    >
                        <b-form-select v-model="data.send_to" name="send_to">
                            <option :value="1">Yes</option>
                            <option :value="0">No</option>
                        </b-form-select>
                    </b-form-group>

                    <div
                        v-if="
                            !_.isEmpty(sourceResources) &&
                            env.VUE_APP_ENV !== 'PROD'
                        "
                    >
                        <b-form-group
                            v-show="showTestAntech"
                            :label-for="`test_antech`"
                            :label-cols="4"
                            label="Test Antech Connection:"
                        >
                            <b-form-select
                                v-model="data.test_antech"
                                :options="testAntechOptions"
                                name="test_antech"
                            ></b-form-select>
                            <span style="margin-bottom: 3px; font-size: 11px;">
                                This field is to test Antech connection and is
                                only available in development mode (QA and
                                Stage)
                            </span>
                        </b-form-group>
                    </div>

                    <b-form-group
                        v-if="!isSoap"
                        class="medical-note"
                        :label-for="`soap`"
                        :label-cols="4"
                        label="Medical Note:"
                    >
                        <b-form-select
                            id="soap"
                            :disabled="is_locked"
                            v-model="data.soap"
                            :options="medicalNotes"
                            name="soap"
                        ></b-form-select>
                    </b-form-group>

                    <b-form-group
                        v-show="lab_record"
                        class="lab-record"
                        :label-for="`received_date`"
                        label="Received:"
                        label-cols="4"
                    >
                        <date-picker
                            @dp-show="showDatePicker"
                            :config="datePickerOptions"
                            :disabled="is_locked"
                            v-model="data.received_date"
                            placeholder="Received Date (optional)"
                            name="received_date"
                        ></date-picker>
                    </b-form-group>

                    <b-form-group
                        v-if="data.filled_by == 1 || data.filled_by == 3"
                        label="Invoice:"
                        class
                        label-cols="4"
                        label-for="bill_to_invoice"
                    >
                        <div class="form-inline">
                            <b-form-select
                                v-if="!is_paid"
                                id="bill_to_invoice"
                                :disabled="is_locked"
                                disabled-field="notEnabled"
                                v-model="data.bill_to_invoice"
                                :options="invoices"
                                name="bill_to_invoice"
                                class="form-control"
                                style="width: 58%; padding-right: 21px;"
                            ></b-form-select>
                            <div
                                v-else
                                class="col-form-label"
                                style="width: 58%; padding-right: 21px;"
                            >
                                {{ invoice_name }}
                            </div>
                            <b-form-checkbox
                                v-if="data.bill_to_invoice != -3"
                                id="is_print_on_invoice"
                                :disabled="is_locked || is_paid || isLoading"
                                v-model="data.is_print_on_invoice"
                                class="btn-m ml-2"
                                name="is_print_on_invoice"
                                plain
                                >Print on Invoice</b-form-checkbox
                            >
                        </div>
                    </b-form-group>

                    <b-form-group
                        v-if="action !== 'edit'"
                        :label-for="`action`"
                        :label-cols="4"
                        label="Actions:"
                    >
                        <v-select
                            id="action"
                            :disabled="is_locked"
                            :options="actionFilter"
                            v-model="data.action"
                            :label="'text'"
                            placeholder="Actions (optional)"
                            name="action"
                            multiple
                            aria-placeholder="true"
                        ></v-select>
                    </b-form-group>

                    <b-form-group
                        class="grooming"
                        v-if="action !== 'refill'"
                        :label-for="`filled_by`"
                        :label-cols="4"
                        label="Filled By:"
                    >
                        <b-form-select
                            v-if="!is_paid"
                            id="filled_by"
                            :disabled="is_locked"
                            v-model="data.filled_by"
                            v-validate="'required'"
                            :options="filledBy"
                            class="col mr-auto"
                            name="filled_by"
                        ></b-form-select>
                        <div v-else class="col-form-label">
                            {{ clinic_name }}
                        </div>
                    </b-form-group>

                    <b-form-group
                        v-if="
                            !checkItemType(
                                data.item_type,
                                ITEMTYPES.TYPES.TYPE_LAB
                            )
                        "
                        label="Lot Number:"
                        label-cols="4"
                        label-for="lot"
                    >
                        <v-select
                            id="lot"
                            v-model="data.lot"
                            :disabled="
                                is_locked ||
                                data.filled_by != 1 ||
                                isCubexItem(data.item) ||
                                isCubex
                            "
                            :options="lotOptionFilter"
                            :label="'text'"
                            placeholder="Lot Number (optional)"
                            name="lot"
                            v-b-tooltip.hover
                            :title="
                                isCubexItem(data.item) || isCubex
                                    ? 'This item is stored and managed in Cubex. The corresponding lot number will be updated in DaySmart once the item is dispensed from the cabinet and it is determined.'
                                    : ''
                            "
                            multiple
                        >
                            <template #list-header>
                                <div class="menu">
                                    <a
                                        @click.stop="addLot"
                                        class="badge badge-success rounded-0"
                                        >Add New</a
                                    >
                                </div>
                            </template>
                        </v-select>
                    </b-form-group>

                    <b-form-group
                        v-show="
                            checkItemType(
                                data.item_type,
                                ITEMTYPES.TYPES.TYPE_VACCINE
                            )
                        "
                        :label-for="`tag`"
                        :label-cols="4"
                        label="Tag No:"
                    >
                        <div class="form-inline">
                            <b-form-input
                                id="tag"
                                :disabled="is_locked"
                                v-model="data.tag"
                                placeholder="Tag Number (optional)"
                                name="tag"
                                class="col mr-auto"
                            ></b-form-input>
                        </div>
                    </b-form-group>

                    <b-form-group
                        v-show="isShowMicrochipField"
                        :label-for="`microchip`"
                        :label-cols="4"
                        label="Chip:"
                    >
                        <div class="form-inline">
                            <b-form-input
                                id="microchip"
                                :disabled="is_locked"
                                v-model="data.microchip"
                                placeholder="Microchip Number (optional)"
                                name="microchip"
                                class="col mr-auto"
                            ></b-form-input>
                        </div>
                    </b-form-group>

                    <b-form-group
                        class="med-record grooming"
                        :label-for="`route`"
                        :label-cols="4"
                        label="Route:"
                    >
                        <b-form-select
                            id="route"
                            :disabled="is_locked"
                            v-model="data.route"
                            :options="routesFilter"
                            class="col mr-auto"
                            name="route"
                        ></b-form-select>
                    </b-form-group>

                    <b-form-group
                        class="med-record grooming"
                        :label-for="`location`"
                        :label-cols="4"
                        label="Location:"
                    >
                        <b-form-select
                            id="location"
                            :disabled="is_locked"
                            v-model="data.location"
                            :options="locationsFilter"
                            class="col mr-auto"
                            name="location"
                        ></b-form-select>
                    </b-form-group>
                    <b-form-group
                        class="med-record grooming"
                        v-if="action !== 'refill' && !isRefillItem"
                        :label-for="`refill`"
                        :label-cols="4"
                        label="Refill:"
                    >
                        <div class="form-inline">
                            <b-form-input
                                id="refill"
                                :disabled="is_locked"
                                v-model="data.refill"
                                v-validate="'numeric|refill_minimum'"
                                :class="{
                                    'border-danger': errors.has('refill')
                                }"
                                name="refill"
                                class="col-2"
                            ></b-form-input>
                            <span class="ml-2 mr-2">time(s) until</span>
                            <date-picker
                                @dp-show="showDatePicker"
                                :config="datePickerOptions"
                                :disabled="is_locked || isRecordWithRefillItem"
                                v-model="data.expire"
                                :placeholder="
                                    'Date (' +
                                    (data.refill > 0
                                        ? 'required'
                                        : 'optional') +
                                    ')'
                                "
                                name="expire"
                                style="width: 52%;"
                                v-validate="{ required: data.refill > 0 }"
                                :class="{
                                    'border-danger': errors.has('expire')
                                }"
                            ></date-picker>
                        </div>
                    </b-form-group>

                    <b-form-group
                        class="grooming"
                        :label-for="`instruction`"
                        :label-cols="4"
                        label="Instructions:"
                    >
                        <editable
                            id="instruction"
                            ref="instruction"
                            name="instruction"
                            v-model="data.instruction"
                            :disabled="is_locked"
                            :defaultHeight="81"
                            className="h-unset"
                            placeholder="Instructions (optional)"
                        ></editable>
                        <span
                            v-bind:style="[
                                instructionCount >= 0
                                    ? { color: 'grey' }
                                    : { color: 'red' }
                            ]"
                            style="font-size: 10px; padding-left: 53%;"
                            >{{ instructionCount }} character(s) remaining</span
                        >
                    </b-form-group>

                    <b-form-group
                        class="med-record grooming"
                        v-if="data.filled_by == 1"
                        :label-for="`printer`"
                        :label-cols="4"
                        label="Label:"
                    >
                        <div class="form-inline">
                            <div class="form-row mx-0">
                                <b-form-select
                                    id="printer"
                                    :disabled="
                                        is_locked ||
                                        isCubexItem(data.item) ||
                                        isCubex
                                    "
                                    v-model="data.printer"
                                    :options="printerList"
                                    v-b-tooltip.hover
                                    :title="
                                        isCubexItem(data.item) || isCubex
                                            ? 'This functionality is not currently available when the Cubex Integration is enabled'
                                            : ''
                                    "
                                    class="col-8"
                                    name="printer"
                                ></b-form-select>
                                <span
                                    class="col-2 d-flex align-items-center justify-content-center"
                                    >X</span
                                >
                                <b-form-input
                                    id="print_quantity"
                                    :disabled="
                                        is_locked ||
                                        isCubex ||
                                        isCubexItem(data.item) ||
                                        data.printer === 0 ||
                                        data.printer === 'no' ||
                                        data.printer === 'local'
                                    "
                                    v-model="data.print_quantity"
                                    name="print_quantity"
                                    class="col-2"
                                    placeholder="Quantity"
                                ></b-form-input>
                            </div>
                        </div>
                    </b-form-group>

                    <b-form-group
                        :label-for="`record_note`"
                        :label-cols="4"
                        label="Notes:"
                    >
                        <editable
                            id="record_note"
                            :disabled="is_locked"
                            v-model="data.record_note"
                            placeholder="Notes (optional)"
                        />
                    </b-form-group>

                    <Attachment
                        v-if="action !== 'edit'"
                        ref="attachment"
                        :isShowSource="false"
                        :disabled="is_locked"
                    ></Attachment>
                </div>
            </b-form>
            <div slot="modal-footer" class="w-100">
                <b-button
                    v-if="!isLoading"
                    @click="$refs.createUpdateRecord.hide('cancel')"
                    class="float-left"
                    variant="secondary"
                    >Cancel</b-button
                >
                <div class="float-right" v-if="treatmentboard">
                    <img v-if="isLoading" src="assets/ajax-loader.gif" />
                    <b-button
                        v-if="
                            !isLoading &&
                            (!isTreatmentboardNew || params.isOpenFromSoap)
                        "
                        :disabled="
                            is_locked ||
                            isHasUsersChooseInvoice ||
                            isNegativeInvoice
                        "
                        @click="save"
                        class="float-left"
                        variant="primary"
                        >{{ action === "edit" ? "Save" : "Complete" }}</b-button
                    >
                    <b-button-group
                        v-else-if="!isLoading && isTreatmentboardNew"
                    >
                        <b-dropdown
                            @click="save"
                            :disabled="is_locked || isHasUsersChooseInvoice"
                            left
                            split
                            variant="primary"
                            text="Complete"
                        >
                            <b-dropdown-item
                                v-if="
                                    _hasAccess(
                                        'schedule.treatmentboard.update'
                                    ) && params.activity.status === 3
                                "
                                @click="
                                    triggerTreatmentboardAction('reactivate')
                                "
                                >Reactivate</b-dropdown-item
                            >
                            <b-dropdown-item
                                v-if="
                                    _hasAccess(
                                        'schedule.treatmentboard.update'
                                    ) && params.activity.status !== 3
                                "
                                @click="triggerTreatmentboardAction('edit')"
                                >Edit</b-dropdown-item
                            >
                            <b-dropdown-item
                                v-if="
                                    _hasAccess(
                                        'schedule.treatmentboard.create'
                                    ) && params.activity.status !== 3
                                "
                                @click="
                                    triggerTreatmentboardAction('duplicate')
                                "
                                >Duplicate</b-dropdown-item
                            >
                            <b-dropdown-item
                                v-if="
                                    _hasAccess(
                                        'schedule.treatmentboard.delete'
                                    ) && params.activity.status === 0
                                "
                                @click="
                                    triggerTreatmentboardAction('deactivate')
                                "
                                >Deactivate</b-dropdown-item
                            >
                            <b-dropdown-item
                                v-if="
                                    _hasAccess(
                                        'schedule.treatmentboard.delete'
                                    ) && params.activity.status === 1
                                "
                                @click="triggerTreatmentboardAction('delete')"
                                >Delete</b-dropdown-item
                            >
                            <b-dropdown-item
                                @click="triggerTreatmentboardAction('history')"
                                >History</b-dropdown-item
                            >
                        </b-dropdown>
                    </b-button-group>
                </div>
                <div class="float-right" v-else>
                    <img v-if="isLoading" src="assets/ajax-loader.gif" />
                    <b-button
                        v-if="!isLoading && action !== 'new'"
                        @click="save(false)"
                        class="float-right"
                        variant="primary"
                        :disabled="isHasUsersChooseInvoice || isNegativeInvoice"
                        >Save</b-button
                    >
                    <b-button-group v-else-if="!isLoading">
                        <b-dropdown
                            v-if="!addNew"
                            :disabled="
                                disableSubmitNewRecommendation ||
                                isHasUsersChooseInvoice
                            "
                            @click="save(false)"
                            left
                            split
                            text="Save + Done"
                            variant="primary"
                        >
                            <b-dropdown-item @click="save(true)"
                                >Save + Add New</b-dropdown-item
                            >
                        </b-dropdown>
                        <b-dropdown
                            v-else
                            @click="save(true)"
                            left
                            split
                            text="Save + Add New"
                            variant="primary"
                            :disabled="isHasUsersChooseInvoice"
                        >
                            <b-dropdown-item @click="save(false)"
                                >Save + Done</b-dropdown-item
                            >
                        </b-dropdown>
                    </b-button-group>
                </div>
                <NewLot ref="newNewLotWindow" @saved="onChange"></NewLot>
            </div>
        </b-modal>
    </div>
</template>
<script>
import {
    union,
    some,
    get,
    difference,
    find,
    forEach,
    isArray,
    filter,
    isEmpty,
    map,
    includes,
    has,
    sortBy,
    assign,
    unionBy
} from "lodash";
import joinPaymentInvoiceSocket from "@/mixins/join-payment-invoice-socket.js";
import vetSelect from "@/module/commons/vet-select.vue";
import vSelect from "vue-select";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import Dosage from "@/module/commons/dosage.vue";
import moment from "moment";
import NewLot from "@/module/commons/inventory/custom-lot-modal";
import Attachment from "@/module/commons/attachment";
import NewRecommendation from "@/components/medical-records/new-recommendation";
import ScheduleHelper from "@/module/schedule/helper";
import RecordHelper from "@/mixins/record.js";
import CubexMixin from "@/mixins/cubex";
import ItemRulesHelper from "@/mixins/item-rules";
import { mapState } from "vuex";
import RecommendationHelper from "@/mixins/recommendation.js";
import ApplyItemRuleCheckbox from "@/components/commons/apply-item-rule-checkbox.vue";
import BillingInvoiceService from "@/services/billing/billing-invoice";
import { DateTime } from "luxon";
import Records from "@/services/records";
import CBILLINGS from "@/constants/billings";
import PatientService from "@/services/patient";
import ITEMTYPES from "@/constants/item-types";
import Editable from "@/components/elements/editable-textarea";
import InvoiceInPaymentWarning from "@/components/invoice/invoice-in-payment-warning";

const DEFAULT_WEIGHT = { weight: null, uom: null };
const WARNING_MESSAGE_NEGATIVE_INVOICE = "Invoice total cannot be negative";

export default {
    name: "createUpdateRecord",
    components: {
        vSelect,
        vetDatetime,
        NewLot,
        Dosage,
        vetSelect,
        Attachment,
        Editable,
        NewRecommendation,
        InvoiceInPaymentWarning,
        ApplyItemRuleCheckbox
    },
    // CERTAIN FUNCTION HAS PLACED IN record.js AS ALSO BEEN USED BY INVOICE>CREATE ITEM
    // IF LATER NEED TO UPDATE THE FLOW, JUST MOVE THE FUNCTION BACK TO HERE
    mixins: [
        ScheduleHelper,
        RecordHelper,
        RecommendationHelper,
        joinPaymentInvoiceSocket,
        CubexMixin,
        ItemRulesHelper
    ],
    props: {
        hideBackdrop: {
            type: Boolean,
            default: false,
            required: false
        },
        page: {
            // Used for VET-6975 when the page is invoice, then apply the socket as for other page will be applied on ticket VET-6981
            type: String,
            required: false
        }
    },
    data() {
        return {
            dataLoaded: false,
            title: "New Record",
            multiple: false,
            continues: true,
            recordNoteContinues: true,
            personalNoteContinues: true,
            loading: false,
            loadingLot: false,
            editable: true,
            is_paid: false,
            invoice_name: "",
            clinic_name: "",
            file: null,
            searchOnlinePharmacyOnly: false,
            instructionCount: 200,
            recordNoteCount: RecordHelper.RECORD_NOTE_COUNT,
            personalNoteCount: RecordHelper.PERSONAL_NOTE_COUNT,
            lab_record: false,
            test_antech: false,
            med_record_rad: false,
            treatmentboard: false,
            addNew: false,
            alertMessages: [],
            isDueError: false,
            hasPatientPrimaryOwner: false,
            hasPatientWeight: false,
            hasClientEmailAddress: false,
            hasClientPhoneNumber: false,
            patient_encoded_id: "",
            client_encoded_id: "",
            key: 0,
            data: {
                prid: null,
                record_id: null,
                item: null,
                patient_id: "",
                client_id: "",
                item_id: "",
                staff_id: null,
                given_by: "",
                diagnosis: [],
                given_date: null,
                next_due: "",
                quantity: 1,
                send_to_device: "",
                source: "",
                send_to: 1,
                received_date: null,
                test_antech: 0,
                soap: -1,
                bill_to_invoice: -1,
                is_print_on_invoice: true,
                previous_is_print_on_invoice: true,
                action: [],
                filled_by: 1,
                lot: [],
                tag: "",
                route: 0,
                location: 0,
                refill: 0,
                expire: null,
                instruction: "",
                printer: "no",
                print_quantity: 1,
                record_note: "",
                personal_note: "",
                file: "",
                item_type: "",
                uom: "",
                item_name: "",
                supplier_id: "",
                sample_id: "",
                lab_result_id: "",
                result_id: "",
                antech_accession_id: "",
                study_id: "",
                refill_summary: null,
                microchip: ""
            },
            datePickerOptions: {
                format: "DD/MM/YYYY",
                widgetParent: "#createUpdateRecord"
            },
            action: "",
            actions: [],
            staffList: [],
            selected: [],
            invoices: [],
            routes: [],
            locations: [],
            item: [],
            itemArr: [],
            parentItemID: null,
            params: [],
            diagnosisOption: [],
            itemLotOption: [],
            medication: [],
            medicalNotes: [],
            printerList: [],
            customLots: [],
            orderedPurchaseIDS: [],
            sourceOptions: [],
            providers: [],
            givenByOptions: [],
            providerCount: 1,
            isEstimate: 0,
            isHeskaCloud: 0,
            vetItemModule: "item",
            vetItemProps: {
                id: "",
                item_type: "",
                module: "patient",
                status: "",
                start: "",
                source: ""
            },
            sendToDeviceOptions: [],
            showSendToLab: true,
            itemNextDue: null,
            itemNextDuePeriod: "days",
            isDisabledSendTo: false,
            isRefillItem: false,
            originalSourcesLength: null,
            sendToLabLabel: "Send to Lab:",
            sourceResources: null,
            showTestAntech: false,
            refill_is_print_on_invoice: true,
            record_invoice: {},
            selectedFirstItem: null,
            defaultPrinter: null,
            formError: [],
            weight: DEFAULT_WEIGHT,
            disableWatcher: false,
            ITEMTYPES: ITEMTYPES,
            hideSendToLab: false, // need this dedicated flag because `showSendToLab` involves mixins. the `hideSendToLab` parameter, when opened, will indeed force hide the send-to-lab field
            isNegativeInvoice: false,
            isTreatmentboardNew: false,
            isQuantityDisabled: false,
            cubexItemDispensingReference: {
                dispensed: null,
                returned: null,
                waste: null
            },
            applyItemRule: true, // checkbox state
            disabledItems: [], // list of item IDs to disable
            tooltipMap: {}, // store tooltip per item
            bufferedOptions: [],
            patientInfo: null
        };
    },
    mounted() {
        // To render the dueOptions before proceed edit/refill due to race condition
        // Checking dueOptions here in case the inventory/getInventoryDueOptions not been dispatch from the parent module hence need to render to get the dueOptions
        if (!this.isDueOptionsRendered) {
            this.$store.dispatch("inventory/getInventoryDueOptions");
        }
    },
    computed: {
        isShowApplyItemRule() {
            return this.editable && !this.is_paid && !this.isActionDuplicate;
        },
        isShowSearchVetsourceProductCatalog() {
            return this.vetsourceEnabled && this.action === "new";
        },
        isActionDuplicate() {
            return this.action === "duplicate";
        },
        isShowMicrochipField() {
            let item = this.data.item;
            if (item) {
                if (Array.isArray(item) && item.length > 0) {
                    item = item[0];
                }
                return this.isItemMicrochip(item);
            }
            return false;
        },
        isActionNewInSoapPage() {
            return this.isSoap && this.action === "new";
        },
        isSoap() {
            return this.$route.name === "soap";
        },
        soapDetails() {
            if (this.isSoap) {
                return this.$store.getters["soap/soapDetails"];
            } else {
                return null;
            }
        },
        isDueOptionsRendered() {
            return this.$store.getters["inventory/isDueOptionsRendered"];
        },
        dueOptions() {
            return this.$store.getters["inventory/dueOptions"];
        },
        actionFilter() {
            return difference(this.actions, this.data.action);
        },
        testAntechOptions() {
            return [
                { value: "0", text: "Submit with no error" },
                { value: "1", text: "Submit with error on first try" },
                { value: "2", text: "Submit with error on second try" }
            ];
        },
        locked() {
            return this.params.activity
                ? this.$store.getters.locked(this.params.activity.id)
                : null;
        },
        is_locked() {
            return some(this.locked);
        },
        isLoading() {
            return this.loading || this.loadingLot;
        },
        isModuleTreatmentBoard() {
            return this.params?.module === this.MODULE.TREATMENT_BOARD;
        },
        isHasUsersChooseInvoice() {
            return this.data.bill_to_invoice
                ? !isEmpty(
                      this.$store.getters["billing/findUserChooseInvoice"](
                          this.data.bill_to_invoice
                      )
                  )
                : false;
        },
        isRecordWithRefillItem() {
            return (
                (this.data.refill_summary &&
                    this.data.refill_summary?.refill_used &&
                    this.data.refill_summary?.refill_used > 0) ||
                (this.data.refill_summary &&
                    this.data.refill_summary.refill_pid == null &&
                    this.data.refill_summary.refills >
                        this.data.refill_summary.remainingRefill)
            );
        }
    },
    beforeRouteEnter(to, from, next) {
        next(self => self.$refs["createUpdateRecord"].show());
    },
    watch: {
        isShowApplyItemRule(value) {
            this.applyItemRule = value;
        },
        "data.bill_to_invoice"(value, oldValue) {
            if (this.dataLoaded) {
                // Follow the item print_on_invoice
                if (value == -3) {
                    this.data.is_print_on_invoice = false;
                } else if (oldValue == -3 && value != -3) {
                    if (this.data.item && this.data.item.is_print_on_invoice) {
                        this.data.is_print_on_invoice =
                            this.data.item.is_print_on_invoice == 1;
                    } else if (
                        Array.isArray(this.data.item) &&
                        this.data.item.length > 0
                    ) {
                        if (this.data.item[0].print_on_invoice) {
                            this.data.is_print_on_invoice =
                                this.data.item[0].print_on_invoice == 1;
                        } else if (this.data.item[0].is_print_on_invoice) {
                            this.data.is_print_on_invoice =
                                this.data.item[0].is_print_on_invoice == 1;
                        }
                    } else {
                        // if no data.item or no print_on_invoice, perhaps when refill item as data.item dont have print_on_invoice
                        this.data.is_print_on_invoice = Boolean(
                            this.previous_is_print_on_invoice
                        );
                    }
                }

                if (value !== -3) {
                    this.joinPaymentInvoice(value);
                }
                this.calculateNegativeInvoiceTotal();
            }
        },
        "data.is_print_on_invoice"(value) {
            this.data.is_print_on_invoice = Boolean(value);
            if (this.data.bill_to_invoice != -3) {
                // to handle refill when select do not invoice->will be untick
                // then reselect other invoice, should follow the previous from refill or item
                this.previous_is_print_on_invoice = value;
            }
            this.calculateNegativeInvoiceTotal();
        },
        "data.item"(value, oldValue) {
            let self = this;

            if (!isArray(value)) {
                this.selectedFirstItem = value;
            }

            //remove next due if select more than 1 item
            if (isArray(value) && value.length > 1) {
                this.data.next_due = "";
            }

            if (value && value.cubex_last_sync != null) {
                this.isCubex = true;
            }

            if (
                value &&
                this.checkItemType(value.item_type, ITEMTYPES.TYPES.TYPE_LAB)
            ) {
                self.initLabFields(value);
            }

            if (value && ("due_date_instring" in value || some(value.due))) {
                self.itemNextDue = null;
                self.data.next_due = null;

                if (self.searchOnlinePharmacyOnly) {
                    if (value.maximumExpirationDate) {
                        self.datePickerOptions.maxDate = this._toDateTime(
                            value.maximumExpirationDate
                        ).toJSDate();
                    } else {
                        self.datePickerOptions.maxDate = null;
                    }

                    if (value.due) {
                        self.data.expire = this._toDateTime(value.due).toFormat(
                            this._getLuxonFormat()
                        );
                    } else {
                        self.data.expire = null;
                    }
                    self.key++;
                } else {
                    let due = (
                        value.due_date_instring || value.due.label
                    ).split(" "); // if item due none, be return "0"
                    if (due.length == 2) {
                        let dueDate = self.data.given_date
                            ? self.data.given_date.clone().add(due[0], due[1])
                            : moment().tz(self._timeZone).add(due[0], due[1]);

                        self.itemNextDue = due[0];
                        self.itemNextDuePeriod = due[1];
                        self.data.next_due = dueDate.format(
                            self.getDateFormat()
                        );
                    }
                }
            } else if (
                Array.isArray(value) &&
                value.length > 0 &&
                value[0].due &&
                value[0].due.value
            ) {
                self.itemNextDue = null;
                self.data.next_due = null;
                let due = value[0].due.label.split(" "); // if item due none, be return "0"
                if (due.length == 2) {
                    let due_date = self.data.given_date
                        ? self.data.given_date.clone().add(due[0], due[1])
                        : moment().tz(self._timeZone).add(due[0], due[1]);

                    self.itemNextDue = due[0];
                    self.itemNextDuePeriod = due[1];
                    self.data.next_due = due_date.format(self.getDateFormat());
                }
            } else if (
                value == null ||
                (Array.isArray(value) && value.length == 0)
            ) {
                self.itemNextDue = null;
                self.itemNextDuePeriod = "days";
                self.data.next_due = null;
                self.data.lot = [];
                self.itemLotOption = [];
            }

            if (
                self.dataLoaded &&
                value &&
                value.id &&
                ["new", "edit", "duplicate", "assign_lab"].includes(self.action)
            ) {
                self.data.lot = [];
                self.itemLotOption = [];

                if (
                    self.checkItemType(
                        value.item_type,
                        ITEMTYPES.TYPES.TYPE_LAB
                    )
                ) {
                    self.getItem(value.id);
                } else {
                    // affected on new, edit, duplicate, assign_lab
                    // not affected refill as it cannt change item
                    // VET-4319: Hide the lot field for lab item hence skip the API to retrieve the lot
                    self.getItemInventoryLot(value.id);
                }
            }

            if (self.dataLoaded) {
                if (value && value.id) {
                    self.data.is_print_on_invoice =
                        value.is_print_on_invoice == 1;
                    self.setPrinterLabel(value.print_label);
                } else if (Array.isArray(value) && value.length > 0) {
                    if (value[0].print_on_invoice) {
                        self.data.is_print_on_invoice =
                            value[0].print_on_invoice == 1;
                    }
                    self.setPrinterLabel(value[0].print_label);
                }
            }

            if (
                self.dataLoaded &&
                value &&
                value.label !== undefined &&
                includes(
                    ["edit", "duplicate", "new", "assign_image"],
                    this.action
                )
            ) {
                self.data.action = [];
                if (value.actions) {
                    self.addActions(value.actions);
                }

                self.data.quantity = value.dose;
                self.data.instruction = self.searchOnlinePharmacyOnly
                    ? value.instructions
                    : value.instruction;
                self.data.location = value.location;
                self.data.route = value.route;
                self.data.item_type = value.item_type;
                self.data.uom = value.uom;
                self.data.item_name = value.display_name;
                self.data.source = value.source;

                // handle next due date, from new created item
                if (value.isNewCreated) {
                    self.data.next_due = self
                        ._toDateTime(self.data.given_date) // ensure date time Datetime object before adding
                        .plus({ seconds: value.dueSeconds })
                        .toFormat(self._dateFormat);
                }

                // DISABLED : same code is already in initLabFields()
                // if (value.source.indexOf("IDEXX_DEVICE:") == 0) {
                //     forEach(value.sources, function (value, key) {
                //         self.sourceOptions.push({
                //             value: "IDEXX_DEVICE:" + key,
                //             text: value
                //         });
                //     });
                //     self.data.source = self.sourceOptions[0].value;
                // }
            }

            if (
                self.dataLoaded &&
                value &&
                isArray(value) &&
                value.length > 1 &&
                includes(
                    ["edit", "duplicate", "new", "assign_image"],
                    this.action
                )
            ) {
                self.setDataAction(value);
            }

            if (this.skipItemWatch) {
                return;
            }

            if (isArray(value) && value.length) {
                return;
            }

            if (
                [
                    "IDEXX",
                    "ZNLABS",
                    "HESKA_CLOUD",
                    "NBIOVET",
                    "MIDWEST",
                    "VETEK",
                    "MICROVET",
                    "BIONOTE"
                ].includes(this.data.source) &&
                self.action == "assign_lab"
            ) {
                // when assign_lab, for source IDEXX, znlabs and heska_cloud should skip below code
                // to avoid it reset to vetItemModule to "item" ..etc
                return;
            }

            if (isArray(oldValue) && isArray(value) && value.length == 0) {
                self.multiple = false;
                self.vetItemModule = "item";
                self.vetItemProps.source = "";
                self.vetItemProps.item_type = "";
                self.itemArr = [];
                self.data.item_type = "";
                self.data.source = "";
            }

            if (value && oldValue && value.id == oldValue.id) {
                return;
            }

            self.checkImageTypeFields(value);

            if (this.searchOnlinePharmacyOnly) {
                this.$nextTick(() => {
                    this.getPersonalNote();
                });
            }
        },
        "data.item_type"(value) {
            let self = this;

            if (value == ITEMTYPES.TYPES.TYPE_LAB) {
                self.multiple = true;
                self.vetItemModule = "item-lab";
                self.vetItemProps.source = this.data.source;
                self.lab_record = true;
                $(".grooming").show();
                $(".med-record").hide();
                $(".lab-record").show();
                if (
                    !isEmpty(self.data.source) &&
                    self.data.source.indexOf("IDEXX_DEVICE:") != 0
                ) {
                    if (
                        self.item.masterItem &&
                        self.item.masterItem.source_reference
                    ) {
                        // disable getting from masterItem because already have in filter_items
                        // forEach(
                        //     self.item.masterItem.source_reference,
                        //     function (value, key) {
                        //         self.sourceOptions.push({
                        //             value: key,
                        //             text: value
                        //         });
                        //     }
                        // );
                    } else {
                        // DISABLED , not in ci. can remove after verified
                        // self.sourceOptions = [];
                    }
                } else {
                    self.vetItemProps.source = "IDEXX_DEVICE";
                }
            } else if (value == 20) {
                $(".grooming").hide();
            } else {
                $(".grooming").show();
                self.multiple = false;
                $(".med-record").show();
                $(".lab-record").hide();
                self.lab_record = false;
            }
        },
        "data.source"(value, oldValue) {
            let self = this;

            self.updateFieldsBasedOnSource("patient-record", value, oldValue);
        },
        "data.send_to"(value) {
            this.checkShowTestAntech(value);
        },
        "data.quantity"(value) {
            // Skip lot handling for Cubex items
            if (this.isCubex) {
                this.data.lot = [];
                return;
            }

            if (this.dataLoaded && value && this.lotOption.length > 0) {
                let unused = this.data.quantity;
                this.data.lot = this.data.lot.filter(lot => !lot.id);
                for (const lot of this.lotOption) {
                    if (
                        unused > 0 &&
                        !find(this.data.lot, ["value", lot.value])
                    ) {
                        this.data.lot.push(lot);
                    }
                    unused = unused - lot.balance;
                }
            }
        },
        "data.staff_id"(value, oldValue) {
            const isLabUseClinicProvider =
                (this.data.item_type == ITEMTYPES.TYPES.TYPE_LAB ||
                    this.data.item_type == ITEMTYPES.TYPES.TYPE_RADIOLOGY ||
                    this.searchOnlinePharmacyOnly) &&
                value &&
                value.is_default_provider;

            if (
                !isLabUseClinicProvider &&
                oldValue &&
                oldValue.is_default_provider
            ) {
                if (
                    this.alertMessages.includes(
                        RecordHelper.LAB_USE_CLINIC_PROVIDER_MSG
                    )
                ) {
                    this.removeAlertMessages(
                        RecordHelper.LAB_USE_CLINIC_PROVIDER_MSG
                    );
                }
                this.removeFormError("staff_id");
            }
            //get personal note when it is vetsource
            if (
                this.searchOnlinePharmacyOnly &&
                JSON.stringify(value) !== JSON.stringify(oldValue)
            ) {
                this.$nextTick(() => {
                    this.getPersonalNote();
                });
            }
        },
        "data.filled_by"(value) {
            let self = this;

            if (value != 1) {
                self.data.printer = "no";
            }
        },
        // Apply item rule checkbox gets auto-unchecked
        searchOnlinePharmacyOnly(newVal) {
            if (newVal) {
                this.applyItemRule = false;
                this.resetDisabledItems();
            }
        }
    },
    updated() {
        // because there are mixins using jQuery to control the visibility of the send-to-lab field,
        // see: updateFieldsBasedOnSource() -> $(".send-to-lab").show(),
        // the DOM no longer uses the data variable from the vue component.
        // so we need to overwrite it again according to the parameter value during the opening .open()
        this.$nextTick(() => {
            this.resetSendToLabVisibility();
        });
    },
    methods: {
        calculateNegativeInvoiceTotal() {
            /**
             * This function will show the warning message of negative invoice but only handle when user
             * - Untick the print on invoice
             * - Invoice still the same as previous invoice
             * - Other scenario will be handle on other enhancement ticket
             *  */

            let showAlert = false;
            if (
                this.data.is_print_on_invoice == false &&
                this.action == "edit" &&
                !this.isLoading // Wait till loading state done
            ) {
                if (
                    this.params?.invoice_item?.invoice_id &&
                    this.data.bill_to_invoice ===
                        this.params?.invoice_item?.invoice_id
                ) {
                    const selectedInvoice = find(this.invoices, {
                        id: this.data.bill_to_invoice
                    });
                    if (selectedInvoice) {
                        let leftAmount =
                            this.roundOff(selectedInvoice.amount) -
                            this.roundOff(this.params.invoice_item.total);
                        showAlert = leftAmount < 0;
                    }
                }
            }

            this.isNegativeInvoice = showAlert;
            if (showAlert) {
                this.pushAlertMessages(WARNING_MESSAGE_NEGATIVE_INVOICE);
            } else {
                this.removeAlertMessages(WARNING_MESSAGE_NEGATIVE_INVOICE);
            }
        },
        setDataAction(item) {
            // VET-5045 - Create as a function to handle the actions from changing item and trigger once when open the treatment board record
            let self = this;
            self.data.action = [];
            forEach(item, function (val, key) {
                if (!isEmpty(val.actions)) {
                    self.addActions(val.actions);
                } else if (!isEmpty(val.action_item)) {
                    self.addActions(val.action_item, true);
                }
            });
        },
        checkItemType(itemType, ITEM_TYPE_TO_MATCHED) {
            return parseInt(itemType) == ITEM_TYPE_TO_MATCHED;
        },
        open(params) {
            this.params = params;
            this.isCubex = this.isCubexItem(params);
            this.action = params.action;
            this.patientInfo = {
                ...params.patient
            };

            this.data.patient_id = params.patient.id;
            this.patient_encoded_id = params.patient.encodedId;
            // this.data.item = params.item ? params.item : [];
            this.data.client_id = params.clientID;
            this.data.refill_summary = {
                refill_pid: params.refill_pid,
                refill_used: params.record_resources?.refill_used,
                ...params.summary
            };

            this.addRefillRule();

            this.isQuantityDisabled =
                params?.record_resources?.quantity_editable == false;

            if (params.patient) {
                this.verifyPatientParams(params.patient);
            }

            if (this.isActionNewInSoapPage && !params.isOpenFromSoap) {
                this.setGivenDateSoap();
            } else if (params.date_order) {
                this.data.given_date = moment.unix(params.date_order);
            } else {
                this.data.given_date = moment().tz(this._timeZone);
            }

            this.datePickerOptions.format = this.getDateFormat();
            this.vetItemProps.start = DateTime.local()
                .setZone(this._timeZone)
                .toFormat(this._dateFormat);

            if (this.isModuleTreatmentBoard) {
                if (this.action === "edit") {
                    this.title = "Edit Record";
                    this.isCubex = this.isCubexItem(params.invoice_item);
                }

                this.treatmentboard = true;
                this.isTreatmentboardNew = this.params.isTreatmentboardNew;
                this.data.record_note = this.params.activity.note;
                this.medication.record_id = params.recordId;
                this.editable = false;
                this.data.item_id = params.item ? params.item.id : "";
                this.data.soap = this.params.treatment.soap_id
                    ? this.params.treatment.soap_id
                    : -1;
            }

            this.data.study_id = params.studyId;
            this.hideSendToLab = params.hideSendToLab === true;

            if (
                this.params.record_resources?.form_data
                    .item_dispensing_reference &&
                this.params.record_resources?.form_data
                    .item_dispensing_reference.length > 0
            ) {
                this.cubexItemDispensingReference = this.params.record_resources?.form_data.item_dispensing_reference[0];
            }

            if (
                this.params.record_resources?.form_data?.record_resource?.Refill
            ) {
                const resource = this.params.record_resources?.form_data
                    ?.record_resource;

                this.data.refill_summary = {
                    refill_pid: resource.RefillID,
                    refill: resource.Refill,
                    remainingRefill: resource.Refill - resource.refill_used,
                    ...this.data.refill_summary
                };
            }

            if (
                !isEmpty(this.params.refill_pid) ||
                this.params.record_resources?.form_data?.record_resource
                    ?.RefillID != null
            ) {
                this.isRefillItem = true;
            }

            if (this.isCubex) {
                this.data.lot = [];
            }

            this.$refs["createUpdateRecord"].show();
        },
        shown() {
            let self = this;
            this.loading = true;
            $(".modal-content").removeAttr("tabindex");
            $(".lab-record").hide();

            let promises = [
                this.getStaffList(),
                this.getStaffListGivenBy(),
                this.getRoutes(),
                this.getLocation(),
                this.getPrinterList(),
                this.getDiagnosis(this.data.patient_id),
                this.data.patient_id
                    ? this.getBilling(this.data.patient_id)
                    : this.setBlankInvoices(),
                this.isModuleTreatmentBoard &&
                    this.getActivity(this.params.activity.id),
                this.data.item_id !== "" && this.getItem(this.data.item_id),
                this.getLatestVital(this.data.patient_id)
            ];

            if (!this.isActionNewInSoapPage) {
                // For soap page and new record no need to has medical note field
                promises.push(this.getMedicalNote(this.data.patient_id));
            }

            //get and set sourceOptions for new
            if (self.action === "new") {
                Records.getCreateDetails(self.params.patient.id).then(
                    response => {
                        if (response.form_data) {
                            self.sourceResources =
                                response.form_data.source_resources;
                            self.setSourceOptions(
                                response.form_data.source_resources
                            );
                        }
                    }
                );
            }

            if (
                this.params.module === this.MODULE.PATIENT ||
                this.params.module === this.MODULE.TREATMENT_BOARD
            ) {
                switch (this.action) {
                    case "assign_image":
                        self.data.supplier_id = this.params.create_resource.supplier_id;
                        self.vetItemProps.source = this.params.lab_data.reference_type;
                        self.vetItemProps.item_type = this.params.lab_data.item_type;
                        self.data.result_id = get(
                            this.params.lab_data,
                            "result_id",
                            null
                        );

                        self.data.bill_to_invoice = -3;
                        break;
                    case "assign_lab":
                        if (has(this.params, "is_lab") && this.params.is_lab) {
                            self.multiple = true;
                            self.data.source = this.params.lab_data.source;
                            self.data.item_type = this.params.is_lab ? 7 : "";

                            if (
                                [
                                    "IDEXX",
                                    "ZNLABS",
                                    "MIDWEST",
                                    "NBIOVET",
                                    "VETEK",
                                    "MICROVET",
                                    "BIONOTE",
                                    "HESKA_CLOUD"
                                ].includes(self.data.source)
                            ) {
                                self.isDisabledSendTo = true;
                            }

                            if (isArray(this.params.lab_data.item_id)) {
                                forEach(this.params.lab_data.item_id, function (
                                    id
                                ) {
                                    promises.push(self.getItem(id));
                                });
                            } else {
                                promises.push(
                                    self.getItem(this.params.lab_data.item_id)
                                );
                            }

                            self.data.result_id = get(
                                this.params.lab_data,
                                "result_id",
                                null
                            );
                            self.data.lab_result_id = get(
                                this.params.lab_data,
                                "lab_result_id",
                                null
                            );
                            self.data.antech_accession_id = get(
                                this.params.lab_data,
                                "antech_accession_id",
                                null
                            );

                            Records.getCreateDetails(self.params.patient.id)
                                .then(response => {
                                    if (response.form_data) {
                                        self.data.supplier_id =
                                            response.form_data.supplier_id;
                                        self.setSourceOptions(
                                            response.form_data.source_resources
                                        );
                                        self.sourceResources =
                                            response.form_data.source_resources;
                                        // ************** COMPARE to CI CODES **************
                                        // here send_to_resources & test_antech_resources
                                        // has already been hardcoded
                                        // ************** COMPARE to CI CODES **************
                                    }
                                })
                                .then(() => {
                                    self.initLabFields({
                                        source: self.data.source
                                    });
                                });
                        }
                        self.data.bill_to_invoice = -3;

                        break;
                    case "edit":
                        this.editRecord(this.params);
                        break;
                    case "duplicate":
                        this.duplicateRecord(this.params);
                        break;
                    case "refill":
                        this.refillRecord(this.params);
                        break;
                }
            }

            Promise.all(promises).then(async () => {
                if (has(this.params, "is_lab") && this.params.is_lab) {
                    self.skipItemWatch = true;
                    self.data.item = self.itemArr;
                    self.data.source = this.params.lab_data.source;
                }

                this.loading = false;
                this.dataLoaded = true;
                if (["new", "duplicate"].includes(this.action)) {
                    await this.$nextTick();
                    self.$refs["select-item"].focus();
                }

                this.setPrinterLabel(
                    this.params.record_resources &&
                        this.params.record_resources.form_data &&
                        this.params.record_resources.form_data.item_resource
                        ? this.params.record_resources.form_data.item_resource
                              .PrintLabel
                        : 0
                );
            });
        },
        reloadPatient(params) {
            this.data.patient_id = params.patient?.id;
            this.patient_encoded_id = params.patient?.encodedId;

            if (params.patient) {
                this.verifyPatientParams(params.patient);
            }
        },
        setSourceOptions(sourceResources) {
            let self = this;

            if (sourceResources.length != 0) {
                forEach(sourceResources, function (value, key) {
                    self.sourceOptions.push({
                        value: key,
                        text: value
                    });
                });
            }
        },
        editRecord(params) {
            this.disableWatcher = true;
            this.title = "Edit Record";

            //VET-5528 - Disable edit the item
            this.editable = false;
            this.setSourceOptions(
                params.record_resources.form_data.source_resources
            );
            this.sourceResources =
                params.record_resources.form_data.source_resources;
            this.data.record_id =
                params.record_resources.form_data.record_resource.ID;
            this.data.bill_to_invoice =
                params.record_resources.form_data.record_resource.invoice_id ||
                -3;
            this.data.is_print_on_invoice = params.invoice_item
                ? params.invoice_item.print_on_invoice == 1
                : 1;
            this.previous_is_print_on_invoice = params.invoice_item
                ? params.invoice_item.print_on_invoice == 1
                : 1;
            this.data.refill =
                params.record_resources.form_data.record_resource.Refill;
            this.data.expire =
                params.record_resources.form_data.record_resource.expire;
            this.data.item_type =
                params.record_resources.form_data.record_resource.Type;
            this.data.route = params.record_resources.form_data.record_resource
                .RouteID
                ? params.record_resources.form_data.record_resource.RouteID
                : 0;
            this.data.location = params.record_resources.form_data
                .record_resource.LocationID
                ? params.record_resources.form_data.record_resource.LocationID
                : 0;
            this.data.item = {
                id: params.record_resources.form_data.item_resource.ID,
                label: params.record_resources.form_data.item_resource.Name,
                display_name:
                    params.record_resources.form_data.item_resource.DisplayName,
                name: params.record_resources.form_data.item_resource.Name
            };
            this.is_paid =
                params.record_resources.form_data.record_resource.is_locked;

            if (
                this.data.bill_to_invoice &&
                this.data.bill_to_invoice != -3 &&
                !this.is_paid
            ) {
                // render invoice details to get invoice status (#VET-2346)
                this.getCurrentInvoice(this.data.bill_to_invoice);
            }
            this.data.uom =
                params.record_resources.form_data.record_resource.uom_value;
            this.invoice_name =
                params.record_resources.form_data.record_resource.invoice_name;
            this.clinic_name =
                params.record_resources.form_data.record_resource.clinic_name;
            // TODO : to check , defined twice in same func , better use item_resource.Type for data.item_type
            this.data.item_type =
                params.record_resources.form_data.item_resource.Type;

            this.data.given_date = moment
                .unix(
                    params.record_resources.form_data.record_resource.RecordDate
                )
                .tz(this._timeZone);

            let selectedDueDuration = find(this.dueOptions, {
                value: params.record_resources.form_data.item_resource.Due
            });
            this.itemNextDuePeriod = selectedDueDuration
                ? selectedDueDuration.text.split(" ")[1]
                : "days";
            if (
                params.record_resources.form_data.record_resource.ReminderDate
            ) {
                let due = moment
                    .unix(
                        params.record_resources.form_data.record_resource
                            .ReminderDate
                    )
                    .tz(this._timeZone);

                if (selectedDueDuration) {
                    this.itemNextDue = selectedDueDuration.text.split(" ")[0];
                } else {
                    this.itemNextDue = due.diff(
                        this.data.given_date,
                        this.itemNextDuePeriod
                    );
                }
                this.data.next_due = due.format(this.datePickerOptions.format);
            }

            this.data.quantity =
                params.record_resources.form_data.record_resource.Quantity;
            this.data.tag =
                params.record_resources.form_data.record_resource.Tag;
            this.data.instruction =
                params.record_resources.form_data.record_resource.Instruction;
            this.data.record_note =
                params.record_resources.form_data.record_resource.RecordNote;
            this.data.personal_note =
                params.record_resources.form_data.record_resource.PersonalNote;
            this.data.microchip =
                params.record_resources.form_data.record_resource.Microchip;
            let soapId =
                params.record_resources.form_data.record_resource.SoapID;
            soapId = soapId != null ? soapId.replaceAll("\u0000", "") : -1;
            this.data.soap = soapId ? soapId : -1;

            this.checkImageTypeFields();
            this.applyLot(params.record_resources);
            this.getItemInventoryLot(params.item_id, true);

            this.data.filled_by =
                params.record_resources.form_data.record_resource.IsClinic;

            // billing will not return invoice with locked status
            // so the invoice option take from here // follow ci
            if (params.record_resources.invoice_id) {
                this.record_invoice = {
                    value: params.record_resources.invoice_id,
                    text: "Invoice #" + params.record_resources.invoice_index
                };
            }
        },
        duplicateRecord(params) {
            let self = this;
            this.title = "Duplicate Record";
            this.data.record_id = null;
            this.data.bill_to_invoice =
                params.record_resources.old_data.record.invoice_id;
            this.data.refill = params.record_resources.old_data.record.Refill;
            this.data.item_type = params.record_resources.old_data.item_type;
            this.data.source = params.record_resources.old_data.source;
            this.data.filled_by = params.record_resources.old_data.is_clinic;
            this.setSourceOptions(
                params.record_resources.old_data.source_resources
            );
            this.sourceResources =
                params.record_resources.old_data.source_resources;

            if (
                !isEmpty(this.data.source) &&
                this.data.source.indexOf("IDEXX_DEVICE:") == 0
            ) {
                forEach(params.record_resources.old_data.sources, function (
                    value,
                    key
                ) {
                    self.sourceOptions.push({
                        value: "IDEXX_DEVICE:" + key,
                        text: value
                    });
                });
                self.data.source = self.sourceOptions[0].value;
            }

            this.data.route = params.record_resources.old_data.record.RouteID
                ? params.record_resources.old_data.record.RouteID
                : 0;
            this.data.location = params.record_resources.old_data.record
                .LocationID
                ? params.record_resources.old_data.record.LocationID
                : 0;
            this.skipItemWatch = true;
            this.getItem(params.record_resources.old_data.item_id, false);
            this.data.is_print_on_invoice = params.invoice_item
                ? params.invoice_item.print_on_invoice == 1
                : 1;
            this.data.item = {
                id: params.record_resources.old_data.item_id,
                label: params.record_resources.old_data.item_name,
                display_name:
                    params.record_resources.old_data.item_display_name,
                name: params.record_resources.old_data.item_name,
                source: params.record_resources.old_data.source,
                item_type: params.record_resources.old_data.item_type,
                sources: params.record_resources.old_data.imaging_sources,
                send_to_lab: params.record_resources.old_data.send_to
            };

            this.data.given_date = moment().tz(this._timeZone);

            this.itemNextDuePeriod = "days";
            if (params.record_resources.old_data.reminder_date) {
                this.data.next_due =
                    params.record_resources.old_data.reminder_date;
                this.itemNextDue = DateTime.fromFormat(
                    params.record_resources.old_data.reminder_date,
                    "MMM dd, yyyy"
                ).diff(
                    DateTime.fromFormat(
                        this.data.given_date.clone().format("MMM DD,YYYY"),
                        "MMM dd,yyyy"
                    ),
                    this.itemNextDuePeriod
                );
            }

            this.data.quantity =
                params.record_resources.old_data.record.Quantity;
            this.data.uom = params.record_resources.old_data.record_uom;
            this.data.instruction =
                params.record_resources.old_data.record.Instruction;
            this.data.record_note =
                params.record_resources.old_data.record.RecordNote;
            this.data.personal_note =
                params.record_resources.old_data.record.PersonalNote;
            this.data.microchip =
                params.record_resources.old_data.record.Microchip;
            if (params.record_resources.old_data.action_item_resources) {
                forEach(
                    params.record_resources.old_data.action_item_resources,
                    function (value, key) {
                        self.data.action.push({
                            value: key,
                            text: value
                        });
                    }
                );
                self.actions = self.data.action;
            }

            let soapId = params.record_resources.old_data.record.SoapID;
            soapId = soapId != null ? soapId.replaceAll("\u0000", "") : -1;
            this.data.soap = soapId ? soapId : -1;
            this.getItemInventoryLot(params.item_id);
            this.checkImageTypeFields();
        },
        refillRecord(params) {
            this.disableWatcher = true;
            let self = this;
            this.title = "New Refill";
            this.editable = false;
            this.item = {
                name: params.record_resources.form_data.item_resource.Name
            };
            this.data.record_id = null;
            this.data.prid =
                params.record_resources.form_data.record_resource.ID;
            this.data.refill =
                params.record_resources.form_data.record_resource.Refill;
            this.data.item_type =
                params.record_resources.form_data.item_resource.Type;
            this.data.route = params.record_resources.form_data.record_resource
                .RouteID
                ? params.record_resources.form_data.record_resource.RouteID
                : 0;
            this.data.location = params.record_resources.form_data
                .record_resource.LocationID
                ? params.record_resources.form_data.record_resource.LocationID
                : 0;

            this.data.is_print_on_invoice =
                params.record_resources.form_data.item_resource
                    .IsPrintOnInvoice == 1;
            this.previous_is_print_on_invoice = this.data.is_print_on_invoice;

            this.data.item = {
                id: params.record_resources.form_data.item_resource.ID,
                label: params.record_resources.form_data.item_resource.Name,
                display_name:
                    params.record_resources.form_data.item_resource.DisplayName,
                name: params.record_resources.form_data.item_resource.Name,
                source:
                    params.record_resources.form_data.record_resource.Source,
                item_type: params.record_resources.form_data.item_resource.Type,
                sources: params.record_resources.form_data.imaging_sources
            };

            this.data.quantity =
                params.record_resources.form_data.record_resource.Quantity;
            this.data.uom =
                params.record_resources.form_data.record_resource.uom_value;
            this.data.instruction =
                params.record_resources.form_data.record_resource.Instruction;
            this.data.record_note =
                params.record_resources.form_data.record_resource.RecordNote;
            this.data.personal_note =
                params.record_resources.form_data.record_resource.PersonalNote;
            this.data.microchip =
                params.record_resources.form_data.record_resource.Microchip;
            if (params.record_resources.form_data.action_item_resources) {
                forEach(
                    params.record_resources.form_data.action_item_resources,
                    function (value, key) {
                        self.data.action.push({
                            value: key,
                            text: value
                        });
                    }
                );
                self.actions = self.data.action;
            }

            this.data.given_date = moment().tz(this._timeZone);
            let selectedDueDuration = find(this.dueOptions, {
                value: params.record_resources.form_data.item_resource.Due
            });
            this.itemNextDuePeriod = selectedDueDuration
                ? selectedDueDuration.text.split(" ")[1]
                : "days";
            if (
                params.record_resources.form_data.record_resource.reminder_date
            ) {
                this.data.next_due =
                    params.record_resources.form_data.record_resource.reminder_date;

                if (selectedDueDuration) {
                    this.itemNextDue = selectedDueDuration.text.split(" ")[0];
                } else {
                    this.itemNextDue = DateTime.fromFormat(
                        params.record_resources.form_data.record_resource
                            .reminder_date,
                        self._dateFormat
                    ).diff(
                        DateTime.fromFormat(
                            this.data.given_date.clone().format("MMM DD,YYYY"),
                            "MMM dd,yyyy"
                        ),
                        this.itemNextDuePeriod
                    )[this.itemNextDuePeriod];
                }
            }

            let soapId =
                params.record_resources.form_data.record_resource.SoapID;
            soapId = soapId != null ? soapId.replaceAll("\u0000", "") : -1;
            this.data.soap = soapId ? soapId : -1;
            this.getItemInventoryLot(params.item_id);
            this.checkImageTypeFields();
        },
        hidden() {
            this.skipItemWatch = false;
            if (this.$route.path === "/schedule/treatment/activity") {
                this.$router.replace("/schedule/treatment");
            }
            this.$emit("hidden");
        },
        billingNote(bill) {
            return (
                "Invoice #" +
                bill.index +
                " (" +
                moment(bill.start.date).format(this.datePickerOptions.format) +
                ")"
            );
        },
        onChange(value) {
            let self = this;
            self.data.lot.push({ text: value.text, value: value.id });
        },
        getStaffList(source) {
            let self = this;
            let lab_source = "";
            if (
                source === "ZNLABS" ||
                source === "VETEK" ||
                source === "MICROVET" ||
                source === "BIONOTE"
            ) {
                self.data.staff_id = null;
                lab_source = "&source=" + source;
            } else if (self.searchOnlinePharmacyOnly) {
                lab_source = "&source=VETSOURCE";
            }
            return this.$http
                .get(
                    process.env.APPS +
                        "index.php/ajax/staff/filter_administrator_veterinarian_sql?search_key=" +
                        lab_source
                )
                .then(function (response) {
                    if (response.data) {
                        self.providers = response.data;
                        self.providers = sortBy(self.providers, ["name"]);
                        if (self.action != "edit")
                            self.setDefaultStaffId(self.providers);
                        return self.providers;
                    }
                })
                .then(function () {
                    self.assignPrevRecordActors(true);
                });
        },
        getStaffListGivenBy(source) {
            let self = this;
            let lab_source = "";
            if (
                source === "ZNLABS" ||
                source === "VETEK" ||
                source === "MICROVET" ||
                source === "BIONOTE"
            ) {
                self.data.given_by = "";
                lab_source = "&source=" + source;
            }
            return this.$http
                .get(
                    process.env.APPS +
                        "index.php/ajax/staff/filter_administrator_veterinarian_sql?given_by=1&search_key=" +
                        lab_source
                )
                .then(function (response) {
                    if (response.data) {
                        self.givenByOptions = response.data;
                        self.givenByOptions = sortBy(self.givenByOptions, [
                            "name"
                        ]);
                        return self.givenByOptions;
                    }
                })
                .then(function () {
                    self.assignPrevRecordActors();
                });
        },
        setDefaultStaffId(staffOptions) {
            let self = this;
            let ignoreDefaultProvider = false; // will always false for patient profile
            let soapProvider = null;
            let currentStaff = find(staffOptions, [
                "id",
                self.$root.currentActor.id
            ]);
            ignoreDefaultProvider = Boolean(currentStaff);

            if (self.isSoap) {
                if (this.params.staffId) {
                    ignoreDefaultProvider = true;
                    soapProvider = find(staffOptions, [
                        "id",
                        this.params.staffId
                    ]);
                }
            }

            if (staffOptions.length == 1) {
                // default the provider if only 1 provider exists
                self.data.staff_id = staffOptions[0];
                self.data.given_by = staffOptions[0];
            } else if (staffOptions.length > 0) {
                const defaultClinicProvider = find(staffOptions, [
                    "is_default_provider",
                    true
                ]);

                // VET-977 - First will be clinic default provider
                if (defaultClinicProvider && !ignoreDefaultProvider) {
                    self.data.staff_id = defaultClinicProvider;
                } else if (currentStaff || soapProvider) {
                    // then default for provider and givenBy will auto select the currentActor
                    self.data.staff_id = soapProvider || currentStaff;
                }
                self.data.given_by = currentStaff;
            } else {
                self.providerCount = 0;
            }
            if (self.isModuleTreatmentBoard) {
                let defaultProvider = null;

                if (self.params.treatment.appointment_id !== null) {
                    self._getAppointment(
                        self.params.treatment.appointment_id
                    ).then(appointment => {
                        let staffId = "";

                        // Need to check appointment.provider into the if condition
                        // Because appointment.provider can be null
                        if (
                            appointment.provider &&
                            find(self.params.treatment.staffs, [
                                "id",
                                appointment.provider.id
                            ])
                        ) {
                            let existingStaff = find(staffOptions, [
                                "id",
                                appointment.provider.id
                            ]);
                            staffId = existingStaff;
                            self.data.staff_id = staffId;
                        } else {
                            let treatmentStaff = sortBy(
                                self.params.treatment.staffs.map(staff => {
                                    return find(staffOptions, {
                                        id: staff.id
                                    });
                                }),
                                ["name"]
                            );
                            staffId = treatmentStaff[0];

                            defaultProvider = _.find(
                                staffOptions,
                                "is_default_provider"
                            );

                            if (currentStaff) {
                                self.data.staff_id = currentStaff;
                            } else if (defaultProvider) {
                                self.data.staff_id = defaultProvider;
                            } else {
                                self.data.staff_id = staffId;
                            }
                        }
                        self.data.given_by = staffId;
                    });
                } else {
                    let treatmentStaff = sortBy(
                        self.params.treatment.staffs.map(staff => {
                            return find(staffOptions, {
                                id: staff.id
                            });
                        }),
                        ["name"]
                    );
                    if (treatmentStaff[0]) {
                        self.data.staff_id = treatmentStaff[0];
                        self.data.given_by = treatmentStaff[0];
                    }

                    defaultProvider = _.find(
                        staffOptions,
                        "is_default_provider"
                    );

                    if (currentStaff) {
                        self.data.staff_id = currentStaff;
                    } else if (defaultProvider) {
                        self.data.staff_id = defaultProvider;
                    }
                }
            }

            if (self.searchOnlinePharmacyOnly) {
                self.data.given_by = null;
            }
        },
        appendEditDiagnoses() {
            if (
                this.params.module === this.MODULE.PATIENT &&
                (this.action == "edit" || this.action == "refill") &&
                this.params.record_resources.form_data.diagnoses_ids
            ) {
                this.data.diagnosis = filter(this.diagnosisOption, option => {
                    return this.params.record_resources.form_data.diagnoses_ids.includes(
                        option.value
                    );
                });
            }
        },
        async getCurrentInvoice(invoiceId) {
            let self = this;
            return await BillingInvoiceService.getInvoice(invoiceId)
                .then(response => {
                    if (response.code === 200) {
                        //Reference:  application/models/orm/TblRecord.php @line-1238
                        self.is_paid =
                            response.resource.status ==
                                CBILLINGS.INVOICE.TYPE_PAID ||
                            response.resource.status ==
                                CBILLINGS.INVOICE.TYPE_WRITEOFF;
                    } else {
                        throw new Error(
                            "Error: Unable to get current invoice."
                        );
                    }
                })
                .catch(e => {
                    console.error(e);
                });
        },
        async getBilling(patientID) {
            let self = this;
            return await this.$http
                .get(
                    process.env.BARRAMUNDI +
                        "patient/" +
                        patientID +
                        "/invoices"
                )
                .then(async function (response) {
                    if (response.data.response.resources) {
                        let otherOptions = [{ value: -1, text: "New Invoice" }];
                        otherOptions =
                            self.data.bill_to_invoice != -3 &&
                            self.action == "edit"
                                ? otherOptions
                                : otherOptions.concat([
                                      {
                                          value: null,
                                          text: "───────────────",
                                          notEnabled: true
                                      },
                                      {
                                          value: -3,
                                          text:
                                              "Do not invoice; do not deduct from inventory"
                                      }
                                  ]);

                        self.invoices = union(
                            response.data.response.resources.map(invoice => ({
                                value: invoice.id,
                                text: !isEmpty(invoice.start)
                                    ? "Invoice #" +
                                      invoice.index +
                                      " (" +
                                      self._carbonDateDisplay(invoice.start) +
                                      ")"
                                    : "Invoice #" + invoice.index + " ()",
                                amount: invoice.amount,
                                id: invoice.id
                            })),
                            otherOptions
                        );
                        if (
                            self.action === "assign_lab" ||
                            self.action === "assign_image"
                        ) {
                            self.data.bill_to_invoice = -3;
                        } else if (self.action !== "edit") {
                            self.data.bill_to_invoice = self.invoices[0].value;

                            if (self.isSoap) {
                                let activeSoapInvoice = await self._activeSoapInvoice(
                                    self.$route.params.id
                                );
                                if (activeSoapInvoice) {
                                    self.data.bill_to_invoice =
                                        activeSoapInvoice.id;
                                }
                            }
                        } else if (self.action == "edit") {
                            // for scenario edit record but the invoice dont return in the api
                            // the record created before the patient assigned new owner
                            // the record`s invoice has been locked, api not return the invoice
                            if (
                                !isEmpty(self.record_invoice) &&
                                !find(self.invoices, function (i) {
                                    return i.value == self.record_invoice.value;
                                })
                            ) {
                                {
                                    self.invoices.unshift(self.record_invoice);
                                }
                            } else if (
                                isEmpty(response.data.response.resources) &&
                                !self.is_paid
                            ) {
                                // VET-3453 - https://daysmartsoftware.atlassian.net/browse/VET-3453
                                // followed how CI assigned either Do not invoice or new invoice.
                                // Refer ticket for tested scenarios.
                                let invoiceId =
                                    self.params?.record_resources?.form_data
                                        ?.record_resource?.invoice_id;
                                self.data.bill_to_invoice = invoiceId ? -1 : -3;
                            }
                        }
                        if (self.data.bill_to_invoice) {
                            self.joinPaymentInvoice(self.data.bill_to_invoice);
                        }
                    }
                });
        },
        async setBlankInvoices() {
            this.invoices = [{ value: -1, text: "New Invoice" }];
        },
        async getActivity(activityID) {
            let self = this;
            await this.$http
                .get(process.env.BARRACUDA + "schedule/activity/" + activityID)
                .then(function (response) {
                    if (response.data.response.resources) {
                        self.medication =
                            response.data.response.resources.medications;

                        if (
                            response.data.response.resources.treatment_board
                                .invoice_id
                        ) {
                            let selectedInvoiceId =
                                self.params?.invoice_item?.invoice_id ||
                                response.data.response.resources.treatment_board
                                    .invoice_id;
                            self.data.bill_to_invoice = find(self.invoices, {
                                value: selectedInvoiceId
                            })
                                ? selectedInvoiceId
                                : -1;
                        } else {
                            self.isEstimate = 1;
                        }

                        if (self.action === "new") {
                            forEach(self.medication, function (data) {
                                self.data.record_id = data.record_id;
                                self.data.quantity = data.quantity;
                                self.data.route = data.route_id;
                                self.data.location = data.location_id;
                                self.data.template_element_id =
                                    data.template_element_id;
                                self.getItem(data.item_id);
                            });
                        }
                    }
                });
        },
        async getItem(itemID, proceedFetchLot = true) {
            let self = this;
            let item_name_lab =
                self.data.item &&
                self.data.item.name &&
                Number(self.data.item.item_type) === ITEMTYPES.TYPES.TYPE_LAB
                    ? self.data.item.name
                    : null;

            if (itemID) {
                await this.$http
                    .get(process.env.BARRAMUNDI + "inventory/" + itemID)
                    .then(function (response) {
                        if (response.data.response.resources) {
                            const resource = response.data.response.resources;
                            const itemLabel = self.decodedHe(
                                item_name_lab ? item_name_lab : resource.name
                            );
                            self.item = assign({ label: itemLabel }, resource);
                            self.isCubex = self.isCubexItem(self.item);

                            if (self.isCubex) {
                                self.data.lot = [];
                                self.itemLotOption = [];
                            }

                            if (
                                self.dataLoaded ||
                                self.isModuleTreatmentBoard
                            ) {
                                self.data.is_print_on_invoice =
                                    self.item.is_print_on_invoice;
                            }
                            self.data.refill = self.item.refill;
                            self.data.item_type = self.item.itemType.id;

                            self.data.source = self.data.source
                                ? self.data.source
                                : self.item.masterItem.source;

                            self.item.code = self.item.masterItem.code;

                            if (
                                self.data.source ||
                                self.data.source === self.item.masterItem.source
                            ) {
                                self.updateFieldsBasedOnSource(
                                    "patient-record",
                                    self.data.source
                                );
                            }

                            if (
                                !includes(
                                    ["duplicate", "refill", "new"],
                                    self.action
                                )
                            ) {
                                self.data.route =
                                    self.item.vaccinationRoute.id != undefined
                                        ? self.item.vaccinationRoute.id
                                        : 0;
                                self.data.location =
                                    self.item.vaccinationLocation.id !=
                                    undefined
                                        ? self.item.vaccinationLocation.id
                                        : 0;
                            }

                            if (
                                self.item.itemType.id ==
                                ITEMTYPES.TYPES.TYPE_LAB
                            ) {
                                if (self.action == "assign_lab") {
                                    self.itemArr.push(self.item);
                                } else {
                                    self.itemArr.push(self.item);
                                    self.data.item = self.itemArr;
                                }
                            } else {
                                self.data.item = isArray(self.item)
                                    ? self.item
                                    : [self.item];
                            }

                            self.checkShowTestAntech(self.data.send_to);
                            if (self.isModuleTreatmentBoard) {
                                // TODO multiple items for lab
                                // self.data.item.push(self.item);
                                if (
                                    !_.find(self.data.item, function (o) {
                                        return o.id == self.item.id;
                                    })
                                ) {
                                    self.data.item = self.item;
                                }

                                // self.editable =
                                //     self.item.itemType.id == 7 ? true : false;
                                // self.multiple =
                                //     self.item.itemType.id == 7 ? true : false;
                                self.setDataAction(self.data.item);
                            }

                            if (self.data.template_element_id) {
                                self.getActionBundle(
                                    self.data.template_element_id
                                );
                            } else {
                                if (self.action == "assign_lab") {
                                    // only action assign_lab need action from here
                                    // action from watch data.item: new, assign_image, duplicate, module treatment board
                                    // action from resource: duplicate, refill

                                    self.actions = self.item.action_item
                                        .filter(action => {
                                            return action.action.id !== 6;
                                        })
                                        .map(action => ({
                                            value:
                                                action.action.id +
                                                "#$" +
                                                action.id,
                                            text:
                                                action.action.id === 7
                                                    ? action.print_actions.label
                                                    : action.action.label
                                        }));
                                }
                            }
                        }

                        if (
                            isArray(self.data.item) &&
                            self.data.item.length === 1 &&
                            self.selectedFirstItem &&
                            self.selectedFirstItem.isNewCreated &&
                            self.vetItemModule == "item-lab" &&
                            self.data.item[0].label
                        ) {
                            // VET-4078: update label from new created item as it contain price, uom & fees information.
                            // This only apply to first item in the array as add item only can be created once for lab type item.
                            self.data.item[0].label =
                                self.selectedFirstItem.label;
                        }

                        return self.item;
                    });

                if (proceedFetchLot) {
                    this.getItemInventoryLot(itemID);
                }
            }

            if (
                self.params.lab_data &&
                self.params.lab_data.source === "HESKA_CLOUD"
            ) {
                self.data.source = self.params.lab_data.source;
                self.data.item_type = ITEMTYPES.TYPES.TYPE_LAB;
                self.itemArr = [];
                self.isHeskaCloud = 1;
            }
        },
        resetSendToLabVisibility() {
            if (this.hideSendToLab === true) {
                let element = document.querySelector(".send-to-lab");
                if (element) {
                    element.style.display = "none";
                }
            }
        },
        async getItemInventoryLot(itemId, fetchOnly = false) {
            // Skip lot handling for both lab items (VET-4319) and Cubex items (VET-8753)
            if (
                !this.checkItemType(
                    this.data.item_type,
                    ITEMTYPES.TYPES.TYPE_LAB
                ) &&
                !this.isCubex
            ) {
                this.loadingLot = true;
                await this.getInventoryLot(itemId, fetchOnly);
                this.loadingLot = false;
            }
        },
        getActionBundle(templateElementID) {
            let self = this;
            if (templateElementID) {
                this.$http
                    .get(
                        process.env.BARRACUDA +
                            "schedule/treatment-actions-template-element/" +
                            templateElementID
                    )
                    .then(function (response) {
                        if (response.data.response.resources) {
                            self.actions = response.data.response.resources.map(
                                action => ({
                                    value: "treatmentBoard" + "#$" + action.id,
                                    text: action.action_type.type
                                })
                            );
                            self.data.action = self.actions;
                        }
                    });
            }
        },
        async save(addNew) {
            let self = this;
            let dataAction = [];
            let dataDiagnosis = [];
            self.customLots = [];
            self.orderedPurchaseIDS = [];
            let valid = await self.$validator.validateAll();
            let validRecommendation = true;

            if (self.searchOnlinePharmacyOnly) {
                validRecommendation = await self.$refs[
                    "new-recommendation"
                ].validate();
            }

            this.checkDueDate();
            // VET-1217 - lab item will not auto select to default provider
            // VET-3232 - new recommendation item should behave like lab item
            let isLabUseClinicProvider =
                (self.data.item_type == ITEMTYPES.TYPES.TYPE_LAB ||
                    self.data.item_type == ITEMTYPES.TYPES.TYPE_RADIOLOGY ||
                    self.searchOnlinePharmacyOnly) &&
                self.data.staff_id.is_default_provider;
            if (isLabUseClinicProvider) {
                this.formError.push("staff_id");
                this.pushAlertMessages(
                    RecordHelper.LAB_USE_CLINIC_PROVIDER_MSG
                );
                return;
            }

            if (this.isDueError) {
                // cannt proceed while due is before given
                return;
            }
            if (
                self.data.lot &&
                self.data.filled_by == 1 &&
                !self.checkItemType(
                    self.data.item_type,
                    ITEMTYPES.TYPES.TYPE_LAB
                )
            ) {
                if (isArray(self.data.lot)) {
                    forEach(self.data.lot, function (value) {
                        if (value.value.search(":") > 0) {
                            self.customLots.push(JSON.parse(value.value));
                        } else {
                            self.orderedPurchaseIDS.push(value.value);
                        }
                    });
                } else {
                    self.orderedPurchaseIDS.push(self.data.lot.value);
                }
            }

            if (self.data.action) {
                forEach(self.data.action, function (data) {
                    dataAction.push(data.value);
                });
            }

            if (self.data.diagnosis) {
                forEach(self.data.diagnosis, function (data) {
                    dataDiagnosis.push(data.value);
                });
            }

            if (self.isActionNewInSoapPage) {
                self.data.soap = self.params.soapId ? self.params.soapId : -1;
            }

            const soapId = self.data.soap !== -1 ? self.data.soap : null;

            if (
                self.recordNoteContinues &&
                self.personalNoteContinues &&
                self.continues &&
                valid &&
                validRecommendation
            ) {
                self.loading = true;

                if (this.searchOnlinePharmacyOnly) {
                    if (this.disableSubmitNewRecommendation) return;
                    return this.saveRecommendation();
                }

                let sendToSelected = self.data.send_to;
                if (self.action === "assign_lab") {
                    sendToSelected = self.data.source === "ABAXIS_FUSE" ? 1 : 0;
                }

                if (self.isCubex || self.isCubexItem(self.data.item)) {
                    self.data.printer = null;
                    self.data.print_quantity = null;
                }

                let post = {
                    id: self.data.record_id,
                    prid: self.data.prid,
                    due: self.data.next_due
                        ? moment(
                              self.data.next_due,
                              self.getDateFormat()
                          ).format(self.getDateFormat())
                        : null,
                    expire: self.data.expire
                        ? moment(self.data.expire, self.getDateFormat()).format(
                              self.getDateFormat()
                          )
                        : null,
                    start: self.data.given_date
                        ? self.data.given_date.format(self.getDateFormat())
                        : null,
                    start_time: self.data.given_date
                        ? self.data.given_date.format("h:mm: A")
                        : self.data.given_date,
                    soap_id: soapId,
                    patient_id: self.params.patient.id,
                    provider_id: self.data.staff_id.id,
                    given_by_id: self.data.given_by
                        ? self.data.given_by.id
                        : null,
                    item: isArray(self.data.item)
                        ? map(self.data.item, "id").join(",")
                        : self.data.item.id,
                    item_name: self.data.item_name,
                    item_type: self.data.item_type,
                    quantity: self.data.quantity,
                    send_to_device: self.data.send_to_device,
                    source: self.data.source,
                    send_to: sendToSelected,
                    received_date: self.data.received_date
                        ? moment(self.data.received_date).format(
                              self.getDateFormat()
                          )
                        : null,
                    uom: self.action === "refill" ? null : self.data.uom,
                    manufacturer: null,
                    lot: self.data.lot ? self.data.lot.id : null,
                    route_id: self.data.route,
                    location_id: self.data.location,
                    reminder: null,
                    instruction: self.data.instruction,
                    note: null,
                    record_note: self.data.record_note,
                    personal_note: self.data.personal_note,
                    refill: self.data.refill,
                    bill_to_invoice: self.data.bill_to_invoice.id // known issue: error if patient has no owner
                        ? self.data.bill_to_invoice.id
                        : self.data.bill_to_invoice,
                    is_print_on_invoice: self.data.is_print_on_invoice ? 1 : 0,
                    clinic: self.data.filled_by,
                    tag: self.data.tag,
                    record: null,
                    print_label: self.data.printer,
                    print_quantity: self.data.print_quantity,
                    iitem_actions:
                        self.action === "refill"
                            ? dataAction.join(",")
                            : dataAction,
                    diagnosis_ids: dataDiagnosis,
                    purchase_ids: self.orderedPurchaseIDS,
                    ordered_purchase_ids: JSON.stringify(
                        self.orderedPurchaseIDS
                    ),
                    custom_lots:
                        self.customLots.length > 0
                            ? JSON.stringify(self.customLots)
                            : "[]", // instead of null or empty array ([]), backend accept only "[]" in string as empty
                    isTreatementBoard: self.treatmentboard,
                    template_element_id: self.medication.template_element_id,
                    supplier_id: self.data.supplier_id,
                    sample_id: self.data.sample_id,
                    lab_result_id: self.data.lab_result_id,
                    result_id: self.data.result_id,
                    antech_accession_id: self.data.antech_accession_id,
                    study_id: self.data.study_id,
                    microchip: self.data.microchip
                };

                let ciUrl = "";

                if (self.action === "refill") {
                    ciUrl = "index.php/patient/refill/save";
                } else {
                    ciUrl = "index.php/patient/prescription/save";
                }

                let response = await $.post(process.env.APPS + ciUrl, post);

                if (response.state == "0") {
                    if (self.action !== "edit") {
                        await self.$refs["attachment"].getFile({
                            record_id:
                                self.action === "refill"
                                    ? response.record_id
                                    : response.lab_id,
                            patient_id: self.params.patient.id,
                            module: "PATIENT",
                            soap_id: soapId,
                            source_id:
                                self.action === "refill"
                                    ? response.record_id
                                    : response.lab_id,
                            source_type: self.data.item_type ? 4 : null, //all 4 if have itemType
                            summary: self.checkItemType(
                                self.data.item_type,
                                ITEMTYPES.TYPES.TYPE_LAB
                            )
                                ? "Lab attachment"
                                : "Record attachment",
                            lab_id: self.checkItemType(
                                self.data.item_type,
                                ITEMTYPES.TYPES.TYPE_LAB
                            )
                                ? self.action === "refill"
                                    ? response.record_id
                                    : response.lab_id
                                : null
                        });
                    }

                    if (response.accession_id && response.antech_token) {
                        self.$root.$emit("call-antech-iframe", {
                            antech_token: response.antech_token,
                            accession_id: response.accession_id
                        });
                    } else if (
                        ((response.lab_ids && response.lab_ids.length > 0) ||
                            response.idexx_lab_id ||
                            response.lab_id) &&
                        self.action !== "assign_lab"
                    ) {
                        let print_lab_params = {
                            idexx_lab_id: response.idexx_lab_id,
                            idexx_url: [response.idexx_url],
                            lab_ids: [response.lab_id],
                            source: response.lab_extension,
                            module: "appointment"
                        };

                        self._print_lab_order(print_lab_params).then(function (
                            lab_response
                        ) {
                            if (lab_response) {
                                if (lab_response.idexx_iframe) {
                                    //load idexx iframe
                                    lab_response.module_url =
                                        print_lab_params.module_url;
                                    lab_response.module =
                                        print_lab_params.module;

                                    self.$root.$emit(
                                        "call_idexx_iframe",
                                        lab_response
                                    );
                                } else {
                                    //load requisition form modal
                                    lab_response.module_url =
                                        print_lab_params.module_url;
                                    lab_response.module =
                                        print_lab_params.module;

                                    self.$root.$emit(
                                        "call-multirequsition-modal",
                                        lab_response
                                    );
                                }
                            }
                        });
                    }
                    self.$socket.client.emit("v-broadcast-all", {
                        action: "refresh-document",
                        patient_id: self.params.patient.id,
                        businessId: this.$root.setting.business_id
                    });
                    self.$root.$emit("treatment-board-refresh", {
                        params: {
                            id: self.medication.treatment_activity_id
                        },
                        treatment_board_id: self.params?.treatment?.id
                    });
                    if (self.data.printer !== "no") {
                        let dataLabel = {
                            id: response.prescription_id,
                            type: response.type,
                            patient_id: response.patient_id,
                            printer_id: response.printer_id,
                            print_quantity: response.print_quantity
                        };
                        this.printLabel(dataLabel);
                    }

                    if (response.action_response) {
                        forEach(response.action_response, function (data) {
                            if (data.uri) {
                                window.open(data.uri);
                            }
                        });
                    }
                    if (self.isModuleTreatmentBoard) {
                        this.updateActivity(response);
                    }

                    if (addNew === true) {
                        let refetchInvoices = false;

                        // -1 = New Invoice
                        if (self.data.bill_to_invoice === -1) {
                            refetchInvoices = true;
                        }

                        self.addNew = true;
                        self.loading = false;
                        self.reset({
                            refetchInvoices
                        });
                        if (self.$refs["select-item"]) {
                            await this.$nextTick();
                            self.$refs["select-item"].focus();
                        }
                    } else {
                        self.$refs.createUpdateRecord.hide();
                    }

                    self._message(response.message, "success");
                    self.$emit("onSuccess", { action: self.action });
                } else {
                    self._message(response.message, "danger");
                    self.loading = false;
                    if (
                        response &&
                        (!response.invoice_status ||
                            response.invoice_status != 1)
                    ) {
                        self.$refs.createUpdateRecord.hide();
                    }
                    if (response.record) {
                        self.$emit("onSuccess", { action: self.action });
                    }
                }
            }
        },
        async updateActivity(response) {
            let self = this;
            let arrayMedication = [];

            $.each(
                isArray(self.data.item) ? self.data.item : [self.data.item],
                function (key, value) {
                    let array = {
                        treatment_medication_id: find(self.medication, {
                            item_id: value.id
                        }).id,
                        item_id: value.id,
                        quantity: self.data.quantity,
                        location_id: self.data.location,
                        route_id: self.data.route,
                        record_id: response.record[value.id].record_id
                    };
                    arrayMedication.push(array);
                }
            );

            let params = {
                treatment_board_id: self.params.treatment.id,
                treatment_activity_type_id: 1,
                due: self.params.activity.due,
                due_type: self.params.activity.due_type,
                note: self.params.activity.note,
                patient_id: self.params.patient.id,
                status: 1,
                complete_at: this._now().toSeconds(),
                complete_by: this.$root.currentActor.id,
                medications: arrayMedication
            };
            response = await this.$http.put(
                process.env.BARRACUDA +
                    "schedule/activity/" +
                    self.params.activity.id,
                params
            );

            self.$root.$emit("treatment-board-refresh", {
                params: response.data.response
            });
        },
        setGivenDateSoap() {
            // From soap.vue - medical note page
            if (this.params.soapDate) {
                this.data.given_date = moment.unix(
                    this._fromDateCarbon(this.params.soapDate).toSeconds()
                );
            }
        },
        reset({ refetchInvoices = false }) {
            let self = this;
            self.$validator.reset();

            if (self.isActionNewInSoapPage) {
                self.setGivenDateSoap();
            } else {
                self.data.given_date = moment().tz(this._timeZone);
            }

            self.data.record_id = null;
            self.data.item = null;
            self.data.item_id = "";
            self.data.diagnosis = [];
            self.data.next_due = "";
            self.data.quantity = 1;
            self.data.soap = -1;
            self.data.send_to_device = "";
            self.data.source = "";
            self.data.send_to = 1;
            self.data.received_date = null;
            self.data.test_antech = 0;
            self.data.action = [];
            self.data.lot = [];
            self.data.tag = "";
            self.data.route = 0;
            self.data.location = 0;
            self.data.refill = 0;
            self.data.expire = null;
            self.data.instruction = "";
            self.data.printer = "no";
            self.data.print_quantity = 1;
            self.data.record_note = "";
            self.data.personal_note = "";
            self.data.file = "";
            self.data.item_type = "";
            self.data.uom = "";
            self.data.is_print_on_invoice = true;
            self.previous_is_print_on_invoice = true;
            self.data.item_name = "";
            self.customLots = [];
            self.orderedPurchaseIDS = [];
            self.vetItemModule = "item";
            self.multiple = false;
            self.vetItemProps = {
                id: "",
                item_type: "",
                module: "patient",
                status: "",
                start: "",
                source: ""
            };
            self.itemArr = [];
            self.itemNextDuePeriod = "days";
            self.itemNextDue = null;

            self.checkImageTypeFields();
            self.$refs["attachment"].resetFile();

            if (refetchInvoices) {
                // re-fetch invoices will automatically select the first option
                // which is the latest invoice created.
                self.getBilling(self.data.patient_id);
            } else {
                self.data.bill_to_invoice =
                    self.invoices.length > 0 ? self.invoices[0].value : -1;
            }
        },
        async checkImageTypeFields() {
            const ASTERIS = "ASTERIS";
            const IDEXX_DR = "IDEXX_DR";
            const SOUNDPACS = "SOUNDPACS";

            let self = this;
            let item = this.data.item;
            let params = this.params;

            self.med_record_rad = false;
            self.sendToDeviceOptions = [];

            if (
                includes(
                    ["new", "duplicate", "refill", "assign_image"],
                    this.action
                ) &&
                item
            ) {
                const itemType = parseInt(item.item_type);

                if (
                    item.id &&
                    item.source &&
                    itemType == ITEMTYPES.TYPES.TYPE_RADIOLOGY &&
                    (item.source.toUpperCase() == ASTERIS ||
                        item.source.toUpperCase() == IDEXX_DR ||
                        item.source.toUpperCase() == SOUNDPACS)
                ) {
                    if (
                        typeof item.sources != "undefined" &&
                        item.sources != null &&
                        item.sources != "null"
                    ) {
                        if (Object.keys(item.sources).length > 1) {
                            forEach(item.sources, function (value, key) {
                                self.sendToDeviceOptions.push({
                                    value: key,
                                    text: value
                                });
                            });

                            self.data.send_to_device =
                                self.sendToDeviceOptions[1].value;
                        }

                        if (!(params.lab_data && params.lab_data.result_id)) {
                            self.med_record_rad = true;
                        }
                    }
                }
            } else if (this.action === "edit") {
                let formData = params.record_resources.form_data;

                let recordResource =
                    params.record_resources && formData.record_resource
                        ? formData.record_resource
                        : null;
                let sendTo =
                    recordResource && recordResource.SendTo
                        ? recordResource.SendTo
                        : null;
                let imageSources =
                    params.record_resources && formData.imaging_sources
                        ? formData.imaging_sources
                        : null;

                if (recordResource && imageSources) {
                    // Hide Send To Lab
                    self.showSendToLab = false;

                    if (
                        isEmpty(recordResource.Source) &&
                        sendTo != 1 &&
                        sendTo != "1"
                    ) {
                        if (Object.keys(imageSources).length != 0) {
                            forEach(imageSources, function (value, key) {
                                self.sendToDeviceOptions.push({
                                    value: key,
                                    text: value
                                });
                            });

                            self.data.send_to_device =
                                self.sendToDeviceOptions[0].value;
                            self.med_record_rad = true;
                        }
                    } else if (
                        recordResource.Source &&
                        (recordResource.Source.toUpperCase() == ASTERIS ||
                            recordResource.Source.toUpperCase() == IDEXX_DR ||
                            recordResource.Source.toUpperCase() == SOUNDPACS) &&
                        (sendTo == 1 || sendTo == "1")
                    ) {
                        forEach(imageSources, function (value, key) {
                            self.data.send_to_device = key;
                        });
                    }
                }
            }
        },
        checkShowTestAntech(value) {
            this.showTestAntech = false;
            if (
                value &&
                value !== 0 &&
                value !== "0" &&
                (this.data.source === "ANTECH_WEST" ||
                    this.data.source === "ANTECH_EAST")
            ) {
                this.showTestAntech = true;
            }
        },
        assignPrevRecordActors(isFieldProvider = false) {
            let params = this.params;
            let self = this;
            let details = null;

            switch (this.action) {
                case "edit":
                case "refill":
                    details = params?.record_resources?.form_data;

                    if (details) {
                        if (isFieldProvider) {
                            self.data.staff_id = !isEmpty(
                                details.provider_resource
                            )
                                ? details.provider_resource
                                : null;
                        } else {
                            self.data.given_by = !isEmpty(
                                details.given_by_resource
                            )
                                ? details.given_by_resource
                                : null;
                        }
                    }

                    break;
                case "duplicate":
                    details = params?.record_resources?.old_data;

                    if (details) {
                        if (isFieldProvider) {
                            self.data.staff_id = !isEmpty(details.provider)
                                ? details.provider
                                : null;
                        } else {
                            self.data.given_by = !isEmpty(details.given_by)
                                ? details.given_by
                                : null;
                        }
                    }

                    break;
            }
        },
        async getLatestVital(patientId) {
            PatientService.getLatestVital(patientId).then(res => {
                if (res && res.resources) {
                    let weight = find(res.resources, { master_vital_id: 4 });
                    this.weight = weight
                        ? { weight: weight.value, uom: weight.master_uom_id }
                        : DEFAULT_WEIGHT;
                }
            });
        },
        async getPersonalNote() {
            let self = this;
            self.loading = true;
            if (
                self.params.patient.id &&
                self.data.item?.name &&
                self.data.staff_id.id
            ) {
                let res = await this.$http.post(
                    process.env.BARRAMUNDI +
                        "record/recommendation/personal-note",
                    {
                        patient_id: self.params.patient.id,
                        item_name: self.data.item.name,
                        provider_id: self.data.staff_id.id
                    }
                );
                const { code, resource } = res.data.response;
                if (code == 200) {
                    self.data.personal_note = resource.personal_note;
                }
            }
            self.loading = false;
        },
        triggerTreatmentboardAction(action) {
            this.$emit("activity-action", action, {
                treatment: this.params.treatment,
                activity: this.params.activity
            });
        },

        // used in vet-select to determine whether item should be clickable
        handleSelectable(item) {
            if (!item || typeof item !== "object" || !("id" in item))
                return true;
            return !this.disabledItems.includes(item.id);
        },

        // triggered when checkbox of Apply item rule toggled
        async onApplyItemRuleToggle() {
            if (this.applyItemRule) {
                if (this.params.patient?.id) {
                    await this.evaluateItemRule();
                    this.removeInvalidItems();
                } else {
                    console.warn(
                        "Patient ID not ready yet. Will re-evaluate later."
                    );
                }
            } else {
                this.resetDisabledItems();
            }
        },
        removeInvalidItems() {
            if (!this.data.item) {
                return;
            }

            let items = Array.isArray(this.data.item)
                ? this.data.item
                : [this.data.item];

            const validItems = items.filter(
                item => !this.disabledItems.includes(item.id)
            );

            if (validItems.length === 0) {
                this.data.item = null;
                this.vetItemModule = "item";
                this.$nextTick(() => {
                    this.$refs["select-item"].clear?.();
                });
            } else {
                this.data.item = Array.isArray(this.data.item)
                    ? validItems
                    : validItems[0];
            }
        },
        resetDisabledItems() {
            this.disabledItems = [];
            this.tooltipMap = {};
        },
        // called when vet-select loads the item options
        onOptionsLoaded(options) {
            this.bufferedOptions = options;
            if (this.applyItemRule && this.params.patient?.id) {
                this.evaluateItemRule();
            }
        },

        // evaluate which items should be disabled based on rule matching
        async evaluateItemRule() {
            this.resetDisabledItems();

            if (!this.applyItemRule || !this.params.patient?.id) {
                console.warn(
                    "Cannot evaluate item rule: invalid or missing patient info",
                    this.params.patient
                );
                return;
            }

            // Combine option lists: buffered options + currently selected item(s)
            let options = this.bufferedOptions || [];

            let selectedItems = this.data.item;
            if (!Array.isArray(selectedItems)) {
                selectedItems = selectedItems ? [selectedItems] : [];
            }

            options = unionBy(options, selectedItems, "id");

            for (const item of options) {
                // call mixins ItemRulesHelper
                const result = await this.checkRuleItem(
                    this.params.patient,
                    item
                );
                if (!result.isPassed) {
                    this.disabledItems.push(item.id);
                    this.tooltipMap[item.id] =
                        result.message ||
                        "This item cannot be selected because it does not fulfill the item rule";
                    item.disabled = true;
                    item.disabledMessage = this.tooltipMap[item.id];
                } else {
                    item.disabled = false;
                    item.disabledMessage = "";
                }
            }
        }
    }
};
</script>
<style scoped>
.vs__dropdown-menu li:hover {
    background-color: #f8d7da !important;
    color: #721c24 !important;
    cursor: not-allowed;
}
</style>
